import { useState } from 'react'

const versions = ['V1', 'V2', 'V3', 'V4', 'V4.1']
const CURRENT_VERSION = 'V4.1'

const versionData = {
  'V1': {
    label: 'V1 — Initial System',
    date: '2024',
    summary: 'FDR Lead Acquisition & Processing System — Operational Infrastructure & Workflow Documentation',
    sections: [
      {
        title: '1. Executive Summary',
        content: `This document outlines the technical architecture and user journey for the FDR Lead Processing System. The system is designed to acquire high-intent leads via digital channels, rigorously qualify them based on debt thresholds, verify their identity via Two-Factor Authentication (2FA), and autonomously route them to appropriate debt relief partners.

The ecosystem utilizes a hybrid of third-party SaaS platforms for the user interface and a proprietary backend server for complex routing logic, credit analysis, and automated follow-up.`
      },
      {
        title: '2. Platform Architecture',
        content: null,
        table: {
          headers: ['Component', 'Function'],
          rows: [
            ['Heroku', 'Secure cloud hosting for the proprietary backend environment.'],
            ['FDR Server', 'Custom "logic engine" managing buyer routing, soft credit pulls, and data logging.'],
            ['ClickFunnels (CF)', 'Frontend web architecture for landing pages and results delivery.'],
            ['Retell AI', 'Advanced conversational voice engine powering the AI Agent ("Jessica").'],
            ['GoHighLevel (GHL)', 'Centralized CRM for survey hosting, lead database management, and SMS/Email automations.'],
            ['Live Chat AI', 'AI-powered support agent embedded on Results Pages to maintain user engagement.'],
          ]
        }
      },
      {
        title: '3. The Lead Journey',
        content: null,
        subsections: [
          {
            title: 'Phase I: Capture & Segmentation',
            items: [
              'Traffic Entry: Leads arrive from digital advertisements to one of three ClickFunnels pages: Long Format, Qualification, or Video Sales Letter.',
              'Initial Qualification: Call-to-action (CTA) buttons trigger a GHL Survey.',
              'Bifurcation: The system automatically splits leads into two distinct workflows based on declared debt — Workflow A: Under $20,000 in unsecured debt. Workflow B: Over $20,000 in unsecured debt.',
            ]
          },
          {
            title: 'Phase II: Verification & Value Delivery',
            items: [
              'TCPA-Compliant 2FA: Users must complete a phone verification step. A code is sent via SMS; the user must enter this into the GHL form to proceed. All capture forms are strictly TCPA compliant.',
              'Trust Generation: The system generates a unique Trust Certificate for the user, increasing brand authority.',
              'Retention: While the user reviews their results, the Live Chat AI is active on-screen to answer questions and maintain interest.',
            ]
          },
          {
            title: 'Phase III: AI Engagement & Partner Routing',
            items: [
              'Automated Trigger: After a short latency period (60 seconds), GHL sends a trigger to the FDR Server, which initiates an outbound call request to Retell AI.',
              'Conversational AI ("Jessica"): Retell AI executes the call. During the conversation, the FDR Server facilitates a "Soft Credit Pull," allowing for real-time qualification without impacting the user\'s credit score.',
              'Buyer Routing: Based on the current hour, the server identifies the appropriate partner and initiates a live transfer via the Retell AI voice interface.',
            ]
          }
        ]
      },
      {
        title: '4. Automation & Exception Handling',
        content: 'The system employs robust automated workflows to handle scenarios where the "Happy Path" (immediate answer) does not occur. These ensure no lead is lost due to unavailability.\n\nNote: For a comprehensive technical breakdown of all triggers and actions, please refer to the Automation List.'
      },
      {
        title: '5. System Logic Diagram',
        diagram: 'Ad Click → CF Landing Page → GHL Survey → Debt Split → 2FA Verify (TCPA) → Results Page (+ Live Chat AI) → GHL Trigger → Retell AI Call → Soft Credit Pull → Buyer Transfer\n\nFallback: If Busy → Jessica Books GHL Calendar → SMS/Email Reminders'
      }
    ]
  },

  'V2': {
    label: 'V2 — Array Form Integration',
    date: '2024–2025',
    summary: 'V2 introduces the Array Form (address + DOB) for server-side soft credit pulls, TrustedForm certification, and a comprehensive GHL automation suite.',
    sections: [
      {
        title: '1. Executive Summary',
        content: `This document outlines the technical architecture and user journey for the FDR Lead Processing System. The system is designed to acquire high-intent leads via digital channels, rigorously qualify them based on debt thresholds, verify their identity via Two-Factor Authentication (2FA), and autonomously route them to appropriate debt relief partners.

The ecosystem utilizes a hybrid of third-party SaaS platforms for the user interface and a proprietary backend server for complex routing logic, credit analysis, and automated follow-up.`
      },
      {
        title: '2. Platform Architecture',
        table: {
          headers: ['Component', 'Function'],
          rows: [
            ['Heroku', 'Secure cloud hosting for the proprietary backend environment.'],
            ['FDR Server', 'Custom "logic engine" managing buyer routing, soft credit pulls, and data logging, built in NodeJS.'],
            ['ClickFunnels (CF)', 'Frontend web architecture for landing pages and results delivery.'],
            ['Retell AI', 'Advanced conversational voice call engine powering the AI Agent ("Jessica").'],
            ['GoHighLevel (GHL)', 'Centralized CRM for survey hosting, lead database management, and SMS/Email automations.'],
            ['Live Chat AI', 'AI-powered support agent embedded on Results Pages to maintain user engagement.'],
          ]
        }
      },
      {
        title: '3. The Lead Journey',
        subsections: [
          {
            title: 'Phase I: Capture & Segmentation',
            items: [
              'Traffic Entry: Leads arrive from digital advertisements to one of three ClickFunnels pages: Long Format, Qualification, or Video Sales Letter.',
              'Initial Qualification: Call-to-action (CTA) buttons trigger a GHL Survey.',
              'Bifurcation: The system automatically splits leads into two distinct workflows — Workflow A: Under $20,000. Workflow B: Over $20,000.',
            ]
          },
          {
            title: 'Phase II: Verification & Value Delivery',
            items: [
              'TCPA-Compliant 2FA (Step 1): Users complete phone verification. A one-time code is sent via SMS; the user enters it into the GHL form to proceed.',
              'TrustedForm Certificate Generation: The system generates a unique TrustedForm Certificate for the user, increasing brand authority.',
              'Array Form (Step 2): A form is revealed requesting the lead\'s date of birth, street address, city, and zip code. Submission triggers the server to facilitate a "soft credit pull".',
              'Retention: Live Chat AI is active on the results page to answer questions and maintain interest.',
            ]
          },
          {
            title: 'Phase III: AI Engagement & Partner Routing',
            items: [
              'Automated Trigger: After 60 seconds, a trigger is sent to the FDR Server, which initiates an outbound call request to Retell AI.',
              'Conversational AI ("Jessica"): Retell AI executes the call. Jessica validates submitted information and sends results to the server.',
              'Buyer Routing: Based on the current hour, the server identifies the appropriate partner and initiates a live transfer.',
            ]
          }
        ]
      },
      {
        title: '4. Automation & Exception Handling',
        content: 'The system employs robust automated workflows to handle scenarios where the "Happy Path" does not occur.',
        table: {
          headers: ['Automation', 'Function'],
          rows: [
            ['V3 - Retell 1.0', 'First touch point. Determines the appropriate automation to route lead to.'],
            ['V3 - Retell - 2fa missed tagging', 'Tags people who missed 2FA'],
            ['V3 - Retell - AI Outbound Attempts', 'Retries leads w/ calls for next 30 days'],
            ['V3 - Retell - After hrs leads comm.', 'Handles communication w/ leads after 11:45 pm'],
            ['V3 - Retell - Agent Schedule a call over call for users', 'Retell agent schedules call for leads'],
            ['V3 - Retell - Ai Agent Call secondary workflow', 'Nurturing SMS for 2fa_missed & sms_optins who didn\'t do credit pull'],
            ['V3 - Retell - Ai Phone call calendar booked', 'Handles SMS for people who booked call and initiates call at booking time'],
            ['V3 - Remove all', 'Removes leads from all automations after lead is sent to buyer'],
            ['V3 - Retell - Ai chatbot calendar booked workflow', 'Handles calls booked via chatbot'],
            ['V3 - Retell - Array form submitted tagging', 'Tags people who submitted array form'],
            ['v3 - Retell - 2fa missed lead integration', 'Adds leads to nurturing SMS and email flows'],
            ['V3 - Retell Call Status Update', 'Updates call status from Retell'],
            ['V3 - [FDR] Update Opportunity Final', 'Updates lead in pipeline'],
            ['v3 Retell - Frozen credit report tagging', 'Tags the lead as a frozen credit report'],
            ['v3 Retell - sms optin tagging', 'Tags people who complete the OTP'],
            ['V3 - Retell - Soft credit pull trial tagging', 'Tags people w/ multiple tries to submit array form'],
            ['V3 - Retell - Call to Complete Soft Credit Pull', 'Adds leads who didn\'t submit the Array form to V3 - Retell 1.0'],
            ['v3 - Retell - Failed Array form empty cart', 'Adds leads who abandon the array retry form to V3 - Retell 1.0'],
            ['V3 - Retell - AI Outbound Attempts bucket 1-3 pt 2', 'Continuous outbound attempts based on the lead\'s time-zone bucket'],
            ['V3 - Retell - Tag Lead When They Answer A Call', 'Tags the lead as answered when generated by the FDR server'],
            ['V3 - Retell - Trigger Server To Create Tag For Answered Calls', 'Sends updated tags for answered calls to the server'],
          ]
        }
      },
      {
        title: '5. System Logic Diagram',
        diagram: 'Ad Click → CF Landing Page → GHL Survey → Debt Split → 2FA Verify / Array Form (TCPA) → Results Page (+ Live Chat AI) → GHL Trigger → Retell AI Call → Soft Credit Pull → Buyer Transfer\n\nFallback: If Busy → Jessica Books GHL Calendar → SMS/Email Reminders'
      },
      {
        title: '6. System Tagging & Logic',
        content: 'The FDR system utilizes a specific tagging architecture to track lead progression and dictate automated decision-making.',
        subsections: [
          {
            title: '6.1 Progress & Recovery Tags',
            table: {
              headers: ['Tag Name', 'Trigger Event', 'Repercussion / Workflows'],
              rows: [
                ['2fa_missed', 'Lead submits the initial survey but has not yet completed the 2FA form.', 'V3 - Retell - Ai Outbound Attempts (Calls), V3 - Retell - FDR Nurturing Email (Emails), V3 - Retell - Ai agent secondary workflow (SMS)'],
                ['sms_optin', 'Lead submits the 2FA form (Step 1 of the Bridge Page). Lead retains this tag until Array Form is submitted.', 'V3 - Retell - Ai Outbound Attempts (Calls), V3 - Retell - FDR Nurturing Email (Emails), V3 - Retell - Ai agent secondary workflow (SMS)'],
                ['submitted_array_form', 'Lead completes the Array Form (Address, DOB, Zip).', 'V3 - Retell - Ai Outbound Attempts (Calls), V3 - Pre-Qualified Nurturing Email (Emails)'],
              ]
            }
          },
          {
            title: '6.2 Credit Analysis Tags',
            items: [
              'soft_credit_pull_1: Applied when a soft credit pull is performed for the first time.',
              'soft_credit_pull_2: Applied if a second pull is performed due to a failure or error in the first attempt.',
              'soft_credit_pull_3: Applied if a third pull is performed due to a failure or error in the second attempt.',
              'soft_credit_pull_failed: Applied when the soft credit pull fails to return data after 3 attempts.',
              'soft_credit_pull_empty_cart: Applied if an error occurs during attempt 1 or 2 and the lead exits the system before attempting a 3rd time.',
            ]
          },
          {
            title: '6.3 Qualification & Intelligence Tags',
            items: [
              'not_qualified: Applied if the soft credit pull reveals the lead\'s debt amount is under $7,000.',
              'frozen_credit_report: Applied when the credit report returns as "NA," indicating the lead has a frozen credit report.',
              'retell_outbound_attempts_1 - n+1: Applied sequentially every time a lead is called.',
              'retell_after_hrs: Applied if a lead opts in after 11:45 PM and before 8:00 AM EST.',
              'chat: Applied if the lead engages with the Live Chat AI on the results pages.',
              'sent to buyer: Applied if the lead is successfully transferred to a buyer.',
              'warm transfer: Applied if the lead was sent to the buyer over the call with Jessica.',
              'api transfer: Applied if the lead was transferred after the system\'s inability to complete a warm transfer after 30 outbound attempts.',
            ]
          }
        ]
      },
      {
        title: '7. System Logic Diagram with Tagging',
        diagram: 'Ad Click → CF Landing Page → GHL Survey (2fa_missed) → 2FA Form (sms_optin) → Array Form (submitted_array_form) → Results Page (+ Live Chat AI) → GHL Trigger → Retell AI Call (retell_outbound_attempt_1–10) → Soft Credit Pull (soft_credit_pull_1–3) → Buyer Transfer (sent to buyer)'
      }
    ]
  },

  'V3': {
    label: 'V3 — Complete Technical Documentation',
    date: 'February 2026',
    summary: 'Full technical architecture, user journey, and operational reference for System Version 3. Introduces Retell AI multi-bot system with 3 distinct bots, Array soft credit pull, CustomerLabs cross-platform tracking, lead scoring model (0–100+ scale), 6 state-based outbound phone numbers, and a comprehensive 21-automation GHL suite.',
    sections: [
      {
        title: '1. Executive Summary',
        content: `The FDR Lead Processing System (Version 3) is designed to:

• Acquire high-intent leads via digital advertising (Meta, landing pages, direct calls)
• Qualify leads based on unsecured debt thresholds and creditworthiness
• Verify identity through Two-Factor Authentication (2FA) and soft credit pulls
• Route qualified leads autonomously to the appropriate debt relief partner via live call transfer or API handoff
• Nurture leads who do not convert immediately through automated SMS, email, and outbound call retry workflows

The ecosystem uses a hybrid of third-party SaaS platforms for the user-facing interface and a proprietary backend server for complex routing logic, credit analysis, buyer assignment, and automated follow-up orchestration.`
      },
      {
        title: '2.1 Technology Stack',
        table: {
          headers: ['Component', 'Function', 'Technology'],
          rows: [
            ['FDR Server', 'Custom "logic engine" managing buyer routing, soft credit pulls, lead registration, tagging, and data logging.', 'Node.js (Express), TypeScript/JavaScript'],
            ['Heroku', 'Secure cloud hosting for the FDR backend server.', 'Heroku Cloud Platform'],
            ['ClickFunnels (CF)', 'Frontend web architecture for landing pages and results delivery.', 'ClickFunnels'],
            ['GoHighLevel (GHL)', 'Centralized CRM for survey hosting, lead database management, calendar bookings, pipeline tracking, and SMS/Email automations.', 'GoHighLevel'],
            ['Retell AI', 'Advanced conversational voice AI engine powering the AI Agent ("Jessica") across 3 bots.', 'Retell AI'],
            ['Array', 'Third-party API used to perform soft credit pulls; returns credit report data without requiring SSN.', 'Array API'],
            ['CustomerLabs', 'Cross-platform tracking and attribution software; tracks leads across ClickFunnels, GHL, and React/Server pages.', 'CustomerLabs CDP'],
            ['Live Chat AI', 'AI-powered support agent embedded on Results Pages to maintain user engagement and answer questions in real time.', 'Live Chat AI'],
          ]
        }
      },
      {
        title: '2.2 How Platforms Connect',
        content: 'The FDR Server acts as the central orchestration hub that bridges all platforms:',
        items: [
          'ClickFunnels → GHL: Landing page CTAs embed GHL survey forms. On submission, lead data flows into GHL\'s CRM.',
          'GHL → FDR Server: GHL automations trigger HTTP webhook requests to the FDR Server when specific events occur (e.g., form submission, tag applied, calendar booking).',
          'FDR Server → Array: The server sends lead PII (name, address, DOB — not SSN) to the Array API and receives credit report data in return.',
          'FDR Server → Retell AI: The server initiates outbound call requests to Retell AI and receives real-time call data (extracted details, call status, buyer match) back via Retell\'s custom function webhooks.',
          'FDR Server → GHL: The server writes tags, updates pipeline stages, registers calendar events, and triggers downstream automations back into GHL.',
          'CustomerLabs: Tracking code is deployed across all user-facing pages and listens for Google DataLayer events to stitch the full user journey.',
        ]
      },
      {
        title: '3. The Lead Journey — Full Lifecycle',
        subsections: [
          {
            title: 'Phase I: Capture & Segmentation',
            items: [
              'Traffic Entry: Leads arrive from digital advertisements (primarily Meta/Facebook) to one of three ClickFunnels landing pages: Long Format (LF), Qualification Page (QP), or Video Sales Letter (NVSL).',
              'Initial Qualification: CTA buttons trigger a GHL Survey embedded on the page.',
              'Bifurcation: The system automatically splits leads — Workflow A: Under $20,000 in unsecured debt. Workflow B: Over $20,000 in unsecured debt.',
            ]
          },
          {
            title: 'Phase II: Verification & Value Delivery',
            items: [
              'TCPA-Compliant 2FA (Step 1): Users must complete phone verification. A one-time code is sent via SMS; successful completion applies the sms_optin tag.',
              'TrustedForm Certificate Generation: The system generates a unique TrustedForm Certificate for each lead, providing proof of consent.',
              'Array Form (Step 2): A secondary form is revealed requesting DOB, street address, city, and zip code. Submission triggers the FDR Server to perform a soft credit pull via the Array API.',
              'Results Page & Retention: Lead is redirected to a results page. Live Chat AI is active on-screen (tagged as "chat" if engaged).',
            ]
          },
          {
            title: 'Phase III: AI Engagement & Partner Routing',
            items: [
              'Automated Trigger: After a 60-second latency period, GHL triggers the FDR Server, which initiates an outbound call request to Retell AI.',
              'Conversational AI ("Jessica"): Retell AI\'s outbound bot executes the call. Jessica confirms identity and submitted details, collects or verifies address/DOB, obtains consent for identity verification, triggers a soft credit pull via the FDR Server, and receives the matched buyer.',
              'Buyer Routing: Based on the current hour, buyer availability, and credit data, the server identifies the appropriate partner. Jessica initiates a live warm transfer to the partner\'s call center.',
              'Fallback: If the lead does not answer, Jessica leaves a voicemail and the system enters the automated retry and nurturing workflows.',
            ]
          },
          {
            title: 'Alternative Entry Flows',
            items: [
              'Meta Form Leads: Leads generated directly from Meta lead forms are ingested into GHL and routed into the V3 pipeline at the appropriate entry point.',
              'Direct Inbound Calls: Leads who call +1 (855) 799-5699 (displayed on landing pages) are handled by the "Inbound Jessica for Landing Pages" bot.',
              'Inbound Callbacks: Leads returning calls to any of the 6 outbound numbers are handled by the "Inbound Jessica" bot.',
            ]
          }
        ]
      },
      {
        title: '4. Backend Server & Routing Engine',
        subsections: [
          {
            title: '4.1 Overview',
            content: 'The FDR Server is the proprietary logic engine at the heart of the system. It is a Node.js (Express) application written in TypeScript/JavaScript, hosted on Heroku.'
          },
          {
            title: '4.2 Core Server Responsibilities',
            table: {
              headers: ['Function', 'Description'],
              rows: [
                ['Lead Registration', 'Receives lead data from GHL webhooks and Retell custom functions; creates/updates lead records.'],
                ['Soft Credit Pull Orchestration', 'Sends lead PII to the Array API, receives and parses credit report data, and stores results.'],
                ['Buyer Assignment', 'Applies business rules (debt amount, time of day, buyer availability, state) to assign the lead to the optimal debt relief partner.'],
                ['Live Transfer Initiation', 'Returns the assigned buyer to Retell AI so the bot can execute a warm transfer to the correct partner\'s phone line.'],
                ['Tag Management', 'Writes and updates tags on lead records in GHL to reflect real-time status.'],
                ['Call Scheduling', 'Receives scheduling requests from the Retell bot and registers them on the GHL calendar.'],
                ['DNC Processing', 'Processes Do-Not-Call requests by tagging the lead and removing them from all active GHL automations.'],
                ['Disqualification', 'Handles leads who fail qualification (e.g., cannot afford $250/month) by tagging and removing from workflows.'],
                ['Phone Number Routing', 'Implements state-based caller ID logic for outbound calls.'],
              ]
            }
          },
          {
            title: '4.3 API Key Management',
            content: 'All API keys for integrated services (Retell AI, Array, GoHighLevel, buyer partner APIs) are stored in a .env file on the Heroku server environment. Access is restricted to authorized developers with Heroku dashboard access.'
          }
        ]
      },
      {
        title: '5. Retell AI Bots — Inventory & Phone Numbers',
        subsections: [
          {
            title: '5.1 Bot Inventory',
            content: 'The system operates 3 Retell AI bots, all voiced as "Jessica." Each serves a distinct purpose in the lead lifecycle.',
            table: {
              headers: ['#', 'Bot Name', 'Type', 'Purpose'],
              rows: [
                ['1', 'Outbound Credit Check Agent Jessica', 'Outbound', 'Makes outbound calls to leads who submitted their info online. Verifies details, performs soft credit pull, and transfers to a buyer.'],
                ['2', 'Inbound Jessica for Landing Pages', 'Inbound', 'Handles inbound calls from the number displayed on ClickFunnels landing pages. Supports both returning users and brand-new callers.'],
                ['3', 'Inbound Jessica', 'Inbound', 'Handles inbound callbacks to any of the 6 outbound phone numbers. Looks up the caller in the CRM and continues the qualification flow.'],
              ]
            }
          },
          {
            title: '5.2 Outbound Phone Numbers (Bot 1)',
            content: 'The system uses 6 phone numbers for outbound calls. 5 numbers are assigned to specific high-volume states so the caller ID matches the lead\'s area code. Round-Robin Logic: When a lead is from a state outside the 5 dedicated states, the server assigns a number via round-robin rotation. If a call is not answered on the first attempt, the next retry comes from a different number.',
            table: {
              headers: ['Phone Number', 'Assigned State', 'Purpose'],
              rows: [
                ['+1 (213) 955-7699', 'California (CA)', 'Outbound calls to CA leads'],
                ['+1 (330) 835-5076', 'Ohio (OH)', 'Outbound calls to OH leads'],
                ['+1 (904) 664-0193', 'Florida (FL)', 'Outbound calls to FL leads'],
                ['+1 (512) 367-5777', 'Texas (TX)', 'Outbound calls to TX leads'],
                ['+1 (773) 295-4374', 'Illinois (IL)', 'Outbound calls to IL leads'],
                ['+1 (855) 780-4538', 'General (Toll-Free)', 'Outbound calls to all other states; also the callback number given in voicemails'],
              ]
            }
          },
          {
            title: '5.2 Inbound Numbers',
            table: {
              headers: ['Phone Number', 'Placement', 'Bot', 'Purpose'],
              rows: [
                ['+1 (855) 799-5699', 'Displayed on CF landing pages (LF, QP, NVSL)', 'Bot 2 — Inbound Jessica for Landing Pages', 'Handles first-time callers and returning users from the website'],
                ['+1 (330) 835-5076, +1 (904) 664-0193, +1 (512) 367-5777, +1 (855) 780-4538, +1 (773) 295-4374, +1 (213) 955-7699', 'All 6 outbound numbers also accept inbound callbacks', 'Bot 3 — Inbound Jessica', 'Handles callbacks from leads who were previously called'],
              ]
            }
          }
        ]
      },
      {
        title: '6. Soft Credit Pull via Array',
        subsections: [
          {
            title: '6.1 What Array Does',
            content: 'Array is the third-party service that enables the system to perform a soft credit pull — a credit inquiry that does NOT affect the lead\'s credit score and does NOT require their SSN.'
          },
          {
            title: '6.2 Request Payload',
            content: 'The FDR Server sends the following lead data to the Array API:',
            items: [
              'First Name',
              'Last Name',
              'Full Address (Street, City, State, Zip Code)',
              'Date of Birth',
              'IP Address',
            ]
          },
          {
            title: '6.3 Response Data Fields',
            table: {
              headers: ['Field Code', 'Description'],
              rows: [
                ['creditScore', 'The lead\'s credit score'],
                ['PT146', 'Total balance on unsecured tradelines'],
                ['PT145', 'Total balance on unsecured credit card tradelines'],
                ['PT187', 'Total balances on currently delinquent tradelines'],
                ['PT184', 'Total number of trades ever delinquent'],
                ['PT132', 'Total amount on Collections'],
                ['PT019', 'Total Mortgage Monthly Payment'],
                ['PT141', 'Total occurrences of 30 days late within the past 12 months on Mortgage trades'],
                ['MT57S', 'Total past due amount of open mortgage trades verified in past 12 months'],
                ['MT36S', 'Months since most recent mortgage delinquency'],
                ['PT137', 'Total amount on Collections in past 36 months; excluding medical'],
                ['PT174', 'Total amount on Collections; excluding medical'],
                ['ST33S', 'Total balance of open student loan trades verified in past 12 months'],
                ['PT130', 'Number of Tax Liens'],
                ['PT147', 'Total amount on Tax Liens filed within the past 36 months'],
                ['PT175', 'Total amount on Tax Liens'],
                ['BC57S', 'Total past due amount of open credit card trades verified in past 12 months'],
                ['PT179', 'Total balance on open credit card trades'],
                ['PT182', 'Total number of open credit card trades'],
                ['PT205', 'Balance on unsecured personal finance accounts, including joint and authorized user accounts'],
              ]
            }
          },
          {
            title: '6.4 Retry Logic',
            content: 'The system allows up to 3 attempts for a soft credit pull. If the first attempt fails (e.g., data mismatch, API timeout), the lead is prompted to re-submit via a retry form. Tags soft_credit_pull_1, soft_credit_pull_2, and soft_credit_pull_3 track each attempt. After 3 failures, the tag soft_credit_pull_failed is applied.'
          },
          {
            title: '6.5 Key Qualification Thresholds',
            items: [
              'Qualified: Unsecured debt ≥ $7,000 — lead is routed to a buyer.',
              'Not Qualified: Unsecured debt < $7,000 — lead receives the not_qualified tag; buyer returned as "False Buyer".',
              'Frozen Credit Report: If the report returns as "NA," the lead is tagged frozen_credit_report.',
            ]
          }
        ]
      },
      {
        title: '7. Tracking Architecture — CustomerLabs & DataLayer',
        subsections: [
          {
            title: '7.1 Why CustomerLabs',
            content: 'The FDR system spans 3 distinct platforms (ClickFunnels, GoHighLevel, and React/Server-rendered pages). Standard analytics tools cannot stitch user sessions across these domains. CustomerLabs solves this by deploying a unified tracking snippet across all pages and listening for Google DataLayer events to attribute the full journey — from ad click to buyer transfer — back to the original Meta campaign.'
          },
          {
            title: '7.2 CustomerLabs Tracking Code',
            content: 'The following snippet is deployed on every user-facing page:',
            code: `<!-- CustomerLabs Tag -->
<script>
!function(t,e,r,c,a,n,s){t.ClAnalyticsObject=a,t[a]=t[a]||[],
t[a].methods=["trackSubmit","trackClick","pageview","identify","track","trackConsent"],
t[a].factory=function(e){return function(){var r=Array.prototype.slice.call(arguments);
return r.unshift(e),t[a].push(r),t[a]}};for(var i=0;i<t[a].methods.length;i++){
var o=t[a].methods[i];t[a][o]=t[a].factory(o)};n=e.createElement(r),
s=e.getElementsByTagName(r)[0],n.async=1,n.crossOrigin="anonymous",
n.src=c,s.parentNode.insertBefore(n,s)}
(window,document,"script","https://cdn.js.customerlabs.co/cl79026oknmil0.js","_cl");
_cl.SNIPPET_VERSION="2.0.0"
</script>
<!-- End of CustomerLabs Tag -->`
          },
          {
            title: '7.3 Event Tracking Map',
            table: {
              headers: ['Event Name', 'Trigger', 'Pages / Forms', 'DataLayer Payload'],
              rows: [
                ['View_content', 'Lead lands on a ClickFunnels page', '/lf1, /qp1, /nvsl1', 'event, url'],
                ['Initiate_checkout', 'Lead opens/interacts with the GHL survey form', 'GHL form embeds (2 form variants)', 'event, first_name, last_name, email, phone'],
                ['Add_to_cart', 'Lead completes 2FA and reaches the validating-data bridge page', '/validating-data', 'event, url'],
                ['Purchase', 'Lead reaches the results page (qualified, over or under $20k)', '/over20-result-re1, /under20-result-re1', 'event, first_name, last_name, email, phone, currency ("usd"), value (debt amount)'],
                ['Unqualified', 'Lead is disqualified (debt below threshold)', '/ddq-lf1', 'event, first_name, last_name, email, phone'],
                ['Contact', 'Lead lands on the "no credit data" page (frozen report or pull failure)', '/no-cred-re1', 'event, first_name, last_name, email, phone'],
              ]
            }
          },
          {
            title: '7.4 DataLayer Code Snippets',
            code: `// View_content
window.dataLayer.push({
  event: "View_content",
  url: window.location.href
});

// Initiate_checkout
window.dataLayer.push({
  event: "Initiate_checkout",
  first_name: first_name,
  last_name: last_name,
  email: email,
  phone: phone
});

// Add_to_cart
window.dataLayer.push({
  event: "Add_to_cart",
  url: window.location.href
});

// Purchase
window.dataLayer.push({
  event: "Purchase",
  first_name: first_name,
  last_name: last_name,
  email: email,
  phone: phone,
  currency: "usd",
  value: amount
});

// Unqualified
window.dataLayer.push({
  event: "Unqualified",
  first_name: first_name,
  last_name: last_name,
  email: email,
  phone: phone
});

// Contact
window.dataLayer.push({
  event: "Contact",
  first_name: first_name,
  last_name: last_name,
  email: email,
  phone: phone
});`
          }
        ]
      },
      {
        title: '8. System Tagging & Logic',
        content: 'The FDR system uses a structured tagging architecture to track lead progression and drive automated decision-making.',
        subsections: [
          {
            title: '8.1 Progress & Recovery Tags',
            table: {
              headers: ['Tag Name', 'Trigger Event', 'Downstream Workflows'],
              rows: [
                ['2fa_missed', 'Lead submits the initial survey but does not complete the 2FA verification form.', 'V3 - Retell - Ai Outbound Attempts (Calls), V3 - Retell - FDR Nurturing Email (Emails), V3 - Retell - Ai Agent Secondary Workflow (SMS)'],
                ['sms_optin', 'Lead successfully completes the 2FA form (Step 1 of the Bridge Page). Lead retains this tag until the Array Form is submitted.', 'V3 - Retell - Ai Outbound Attempts (Calls), V3 - Retell - FDR Nurturing Email (Emails), V3 - Retell - Ai Agent Secondary Workflow (SMS)'],
                ['submitted_array_form', 'Lead completes the Array Form (Address, DOB, Zip).', 'V3 - Retell - Ai Outbound Attempts (Calls), V3 - Pre-Qualified Nurturing Email (Emails)'],
              ]
            }
          },
          {
            title: '8.2 Credit Analysis Tags',
            table: {
              headers: ['Tag Name', 'Description'],
              rows: [
                ['soft_credit_pull_1', 'Applied when a soft credit pull is performed for the first time.'],
                ['soft_credit_pull_2', 'Applied if a second pull is performed due to a failure or error in the first attempt.'],
                ['soft_credit_pull_3', 'Applied if a third pull is performed due to a failure or error in the second attempt.'],
                ['soft_credit_pull_failed', 'Applied when the soft credit pull fails to return data after 3 attempts.'],
                ['soft_credit_pull_empty_cart', 'Applied if an error occurs during attempt 1 or 2 and the lead exits the system before attempting a 3rd time.'],
              ]
            }
          },
          {
            title: '8.3 Qualification & Intelligence Tags',
            table: {
              headers: ['Tag Name', 'Description'],
              rows: [
                ['not_qualified', 'Applied if the soft credit pull reveals the lead\'s unsecured debt is under $7,000.'],
                ['frozen_credit_report', 'Applied when the credit report returns as "NA," indicating the lead has a frozen credit report.'],
                ['retell_outbound_attempts_1 through n+1', 'Applied sequentially each time a lead is called (e.g., retell_outbound_attempt_2 indicates two calls have been made).'],
                ['retell_after_hrs', 'Applied if a lead opts in after 11:45 PM and before 8:00 AM EST.'],
                ['chat', 'Applied if the lead engages with the Live Chat AI on the results pages.'],
                ['sent_to_buyer', 'Applied when the lead is successfully transferred to a buyer.'],
                ['warm_transfer', 'Applied when the lead was transferred to the buyer live over the call with Jessica.'],
                ['api_transfer', 'Applied when the lead was transferred after the system\'s inability to complete a warm transfer after 30 outbound attempts.'],
              ]
            }
          }
        ]
      },
      {
        title: '9. Automation & Exception Handling',
        table: {
          headers: ['Automation Name', 'Function'],
          rows: [
            ['V3 - Retell 1.0', 'First touch point. Determines the appropriate downstream automation to route the lead to.'],
            ['V3 - Retell - 2fa missed tagging', 'Tags leads who submitted the survey but missed the 2FA step.'],
            ['V3 - Retell - AI Outbound Attempts', 'Retries leads with outbound calls for the next 30 days following initial submission.'],
            ['V3 - Retell - After hrs leads comm.', 'Handles communication with leads who enter the system after 11:45 PM EST.'],
            ['V3 - Retell - Agent Schedule a call over call for users', 'Processes call-back scheduling requests made by the lead during a Retell call.'],
            ['V3 - Retell - Ai Agent Call secondary workflow', 'Nurturing SMS flow for 2fa_missed and sms_optin leads who have not completed the credit pull.'],
            ['V3 - Retell - Ai Phone call calendar booked', 'Handles SMS reminders for leads who booked a callback, and initiates the call at the booked time.'],
            ['V3 - Remove all', 'Removes leads from all active automations after the lead is successfully sent to a buyer.'],
            ['V3 - Retell - Ai chatbot calendar booked workflow', 'Handles callbacks booked via the Live Chat AI bot.'],
            ['V3 - Retell - Array form submitted tagging', 'Tags leads who successfully submitted the Array form.'],
            ['v3 - Retell - 2fa missed lead integration', 'Adds 2fa_missed leads to nurturing SMS and email flows.'],
            ['V3 - Retell Call Status Update', 'Updates call status records from Retell in GHL.'],
            ['V3 - [FDR] Update Opportunity Final', 'Updates the lead\'s position in the GHL pipeline.'],
            ['v3 Retell - Frozen credit report tagging', 'Tags leads with a frozen credit report.'],
            ['v3 Retell - sms optin tagging', 'Tags leads who complete the OTP/2FA verification.'],
            ['V3 - Retell - Soft credit pull trial tagging', 'Tags leads with multiple Array form submission attempts.'],
            ['V3 - Retell - Call to Complete Soft Credit Pull', 'Adds leads who didn\'t submit the Array form to the V3 - Retell 1.0 entry automation.'],
            ['v3 - Retell - Failed Array form empty cart', 'Adds leads who abandon the Array retry form to V3 - Retell 1.0.'],
            ['V3 - Retell - AI Outbound Attempts bucket 1-3 pt 2', 'Manages continuous outbound attempts based on the lead\'s time-zone bucket.'],
            ['V3 - Retell - Tag Lead When They Answer A Call', 'Tags the lead as "answered" when generated by the FDR server.'],
            ['V3 - Retell - Trigger Server To Create Tag For Answered Calls', 'Sends updated tags for answered calls to the server.'],
          ]
        }
      },
      {
        title: '10. System Flow Diagrams',
        subsections: [
          {
            title: '10.1 Primary Lead Journey (Happy Path)',
            diagram: `Ad Click
→ ClickFunnels Landing Page (LF / QP / NVSL)
→ GHL Survey Submission [tag: 2fa_missed]
→ 2FA Phone Verification [tag: sms_optin]
→ Array Form (DOB, Address, Zip) [tags: submitted_array_form, soft_credit_pull_1]
→ Results Page (+ Live Chat AI) [tag: chat if engaged]
→ GHL Trigger (60s delay)
→ FDR Server initiates Retell AI Outbound Call [tag: retell_outbound_attempt_1]
→ Jessica verifies details on call
→ Buyer Assignment by Server
→ Live Warm Transfer to Buyer [tags: sent_to_buyer, warm_transfer]`
          },
          {
            title: '10.2 Fallback & Recovery Paths',
            diagram: `If Lead Does Not Answer Call:
→ Jessica leaves voicemail
→ Lead enters V3 - Retell - AI Outbound Attempts (retries over 30 days)
→ Tags increment: retell_outbound_attempt_2, _3, ... _n

If Lead Requests Callback:
→ Jessica triggers schedule_call function
→ GHL calendar event created
→ V3 - Retell - Ai Phone call calendar booked sends SMS reminder
→ Call initiated at booked time

If Lead Enters After Hours (11:45 PM – 8:00 AM EST):
→ Tag: retell_after_hrs
→ V3 - Retell - After hrs leads comm. handles messaging
→ Call scheduled for next available window

If Soft Credit Pull Fails:
→ Retry up to 3 times [tags: soft_credit_pull_1, soft_credit_pull_2, soft_credit_pull_3]
→ If all fail: tag soft_credit_pull_failed
→ If lead abandons before 3rd attempt: tag soft_credit_pull_empty_cart

If Lead Not Qualified (debt < $7,000):
→ Buyer returned as "False Buyer"
→ Tag: not_qualified
→ Jessica informs lead; call ends gracefully

If Lead Requests DNC:
→ Jessica confirms opt-out
→ tag_dnc function called
→ Lead removed from all workflows

If Warm Transfer Fails After 30 Attempts:
→ Lead transferred via API handoff
→ Tag: api_transfer`
          },
          {
            title: '10.3 Inbound Call Flow (Landing Page)',
            diagram: `Lead calls +1 (855) 799-5699 from landing page
→ Inbound Jessica for Landing Pages answers

If Returning User:
  → Lookup by phone or email via FDR Server
  → If found: proceed to Qualification & Verification
  → If not found: treat as New User

If New User:
  → Collect: first name, last name, email, phone, state, debt amount, credit score
  → Trigger register_new_user
  → Proceed to Qualification & Verification

Qualification & Verification:
  → $250/month check
  → Consent for identity verification
  → Collect DOB, Address, City, Zip (one at a time)
  → Trigger Soft Credit Pull
  → Buyer assignment & live transfer (or graceful exit)`
          },
          {
            title: '10.4 Inbound Callback Flow',
            diagram: `Lead calls back one of the 6 outbound numbers
→ Inbound Jessica answers
→ Lookup by phone or email via FDR Server
→ If found: proceed to Qualification & Verification
→ If not found: New User flow (collect details, register)
→ Same Qualification & Transfer flow as 10.3`
          }
        ]
      },
      {
        title: 'Appendix A: Retell Bot Prompts',
        subsections: [
          {
            title: 'A.1 — Outbound Credit Check Agent Jessica (Bot 1)',
            content: 'Type: Outbound | Purpose: Call leads who submitted their info online. Verify details, perform soft credit pull, and transfer to a matched buyer.',
            code: `AGENT ROLE & OBJECTIVE
You are Jessica, an empathetic and compliant Debt Relief Virtual Assistant working for Final Debt Relief (FDR). Your mission: perform outbound calls to leads who submitted their info, verify their submitted details, verify their identity through a soft credit check, then transfer them live to a debt relief specialist.

Always start the call by greeting the person by name and stating the purpose of the call:
"I'm reaching out because based on the information you provided, you are eligible for a debt relief review. Congratulations — that's a big first step."
Wait for their response then: "This call is just to confirm a few details so I can connect you with a debt relief specialist who will walk you through your personalized options."

SOFT CREDIT PULL FLOW
1. Confirm Identity: "Before we move forward, I just need to confirm some details. Your full name on the application is {{name}}, is that correct?"
2. Confirm Debt Amount: "Perfect. And I have your unsecured debt listed as {{debt_amount}} — is that correct?"
3. Monthly Ability Check: "Great. One more quick question — most programs require that you're able to maintain a minimum payment of at least $250 per month. Is that something you're able to manage comfortably?" (If NO → gracefully exit and trigger disqualify_lead)
4. Consent Script: "Before I connect you with a debt relief specialist, I just need to quickly verify your identity and confirm your unsecured debt amount. This doesn't affect your credit score in any way. Do I have your permission?"

Never use the term "soft credit pull" on a call. Use "debt relief specialist" instead of "debt consolidation specialist."

Validate email → Validate phone → Collect/validate DOB (YYYY-MM-DD) → Collect/validate street address, city, zip code. Always wait for confirmation before proceeding.

Trigger extract_user_details to store all collected information. IT IS MANDATORY to verify email, phone, DOB, street address, city, and zip code before triggering the soft credit pull.

Trigger the "Soft credit pull" action. While waiting (~10 seconds): "I'm just checking your details in the system."

If buyer returned → "Great news — you qualify. I'm now going to connect you directly with a debt consolidation specialist from {{buyer}}."
If buyer = "False Buyer" → "We're unable to enroll you at this time. An agent will contact you shortly."
If buyer = null → "It looks like we weren't able to match your profile to a program right now."

VOICEMAIL DETECTION RULE
If you hear "Please leave a message...", "At the tone...", "Mailbox...", "Sorry we missed you...", long silence followed by a beep, Apple Live Voicemail transcript, or a greeting that never pauses → assume voicemail. Immediately switch to the voicemail script.

APPLE CALL SCREENING RULE
If you hear: "If you record your name and reason for calling, I will see if this person is available..." → provide one short message: "It's Jessica from Final Debt Relief — I have those lower monthly numbers you requested on your assessment. I'm ready to lock them in for you now." Then WAIT silently.

VOICEMAIL SCRIPT (Use exactly as written):
"Hi, this is Final Debt Relief calling about your recent request for help with unsecured debt. We tried reaching you to review your information and connect you with a debt relief specialist. Please call us back at 855-780-4538 at your earliest convenience. Again, that number is 855-780-4538. Thank you."

SCHEDULING LOGIC
If user is unavailable → run get_current_time then ask for callback time. If they say "tomorrow" or "Monday" without a time, assume 5PM–8:30PM. Allowed hours: 8AM–11PM. Run schedule_call.

DO NOT CALL LOGIC
If user asks not to be called again → confirm: "My sincerest apologies, do you wish to stop receiving calls from us?" If yes → call tag_dnc, thank them, end call.`
          },
          {
            title: 'A.2 — Inbound Jessica for Landing Pages (Bot 2)',
            content: 'Type: Inbound | Purpose: Handle inbound calls from the landing page phone number. Support both first-time callers and returning users.',
            code: `AGENT ROLE & OBJECTIVE
You are Jessica, an empathetic and compliant Debt Relief Virtual Assistant for Final Debt Relief (FDR). Your mission is to identify callers, collect or verify their information, and qualify them for a debt relief specialist.

CALL OPENING (Inbound)
"Thank you for calling Final Debt Relief — this is Jessica. Who am I speaking with?"

Once they provide a name: "Thanks, {{user_first_name}}. To make sure I get you to the right place — are you calling us for the first time today, or have you already submitted a debt relief review on our website before?"

If first-time caller → run NEW USER FLOW.
If returning user → "Thanks, {{user_first_name}}. Let me pull up your file — may I have the phone number you used on your application?"

PATH A: CALLER FOUND IN CRM
"I see you here! You are eligible for a debt relief review." Confirm debt, proceed to Qualification & Verification.

PATH B: CALLER NOT FOUND (NEW USER FLOW)
Collect one at a time: first name, last name, email, phone, state (2-letter code), unsecured debt amount, credit score (numeric only).
Trigger register_new_user. Proceed to Qualification & Verification.

QUALIFICATION & VERIFICATION (Both Paths)
1. $250/month check. If NO → gracefully exit, trigger disqualify_lead.
2. Consent for identity verification.
3. Collect one at a time: email, phone, DOB, street address, city and zip.
Trigger Soft Credit Pull. DOB must be sent in YYYY-MM-DD format.

CALL CLOSING
Success (buyer ≠ "False Buyer"): "Great news, you're pre-qualified! You have been matched with {{buyer}}. I'm now going to connect you directly with a live debt relief specialist."
False Buyer: "Unfortunately, our programs require more than $7,000 in unsecured debt to proceed."

BEHAVIOR RULES: Always say "debt relief specialist." Never imply you dialed them. No financial/legal advice.`
          },
          {
            title: 'A.3 — Inbound Jessica (Bot 3)',
            content: 'Type: Inbound | Purpose: Handle inbound callbacks to outbound phone numbers. Look up callers in the CRM and continue qualification.',
            code: `AGENT ROLE & OBJECTIVE (INBOUND VERSION)
You are Jessica, an empathetic and compliant Debt Relief Virtual Assistant for Final Debt Relief (FDR).

CALL OPENING
"Thank you for calling Final Debt Relief — this is Jessica. Who am I speaking with?"

Once they provide a name: "Thanks, {{user_first_name}}. Let me pull up your file — may I have the phone number you used on your application?"

CRITICAL: This is INBOUND. Never say "I'm calling because..." or imply you dialed them.

IF FOUND IN SYSTEM:
"I see, you are eligible for a debt relief review. Congratulations." Confirm debt, then proceed.
$250/month check. If NO → exit, trigger disqualify_lead.
Consent, then collect email, phone, DOB, street address, city and zip (one at a time, repeat each for confirmation).
Trigger Soft Credit Pull. DOB in YYYY-MM-DD format.

IF NOT FOUND (NEW USER):
Collect: first name, last name, email, phone, state (2-letter code), debt amount, credit score (numeric only). Trigger register_new_user. Then proceed to $250 check, consent, detail collection, and soft credit pull.

CALL CLOSING: Same as Bot 2 (success / False Buyer / no match).

FAIL-SAFE RULES: Never guess provider name. Never quote savings percentages. Never mention credit scores, credit impact, taxes, lawsuits, or legal topics. If caller gets upset: "I understand, and I want to get you the right help. Let me connect you with a specialist who can assist you immediately."`
          }
        ]
      },
      {
        title: 'Appendix B: Retell Custom Functions Reference',
        subsections: [
          {
            title: 'B.1 — Outbound Credit Check Agent Jessica (Bot 1)',
            table: {
              headers: ['Function Name', 'Purpose'],
              rows: [
                ['extract_user_details', 'Extracts and stores the lead\'s verified details (email, phone, DOB, address, city, zip) from the conversation.'],
                ['schedule_call', 'Sends the lead\'s preferred callback date/time to the FDR Server, which formats it and registers it on the GHL calendar.'],
                ['get_current_time', 'Retrieves the current date and time (ISO format) so Jessica can calculate callback scheduling.'],
                ['soft_credit_pull', 'Sends extracted lead address, DOB, and full name to the FDR Server to perform a soft credit pull, buyer assignment, and buyer transfer initiation.'],
                ['disqualify_lead', 'Disqualifies the lead and removes them from all active workflows within GHL.'],
                ['tag_dnc', 'Tags the lead as Do Not Call and removes them from all workflows within GHL.'],
                ['national_debt_relief_live_transfer', 'Transfers the lead live to National Debt Relief\'s call center.'],
                ['accredited_debt_relief_live_transfer', 'Transfers the lead live to Accredited Debt Relief\'s call center.'],
                ['first_choice_debt_relief_live_transfer', 'Transfers the lead live to First Choice Debt Relief\'s call center.'],
              ]
            }
          },
          {
            title: 'B.2 — Inbound Jessica for Landing Pages (Bot 2) & Inbound Jessica (Bot 3)',
            content: 'Both inbound bots share the same function set:',
            table: {
              headers: ['Function Name', 'Purpose'],
              rows: [
                ['get_user_by_email', 'Retrieves the lead\'s details from the FDR Server using their email address.'],
                ['extract_user_phone', 'Extracts the lead\'s phone number from the conversation.'],
                ['get_user_by_phone', 'Retrieves the lead\'s details from the FDR Server using their phone number.'],
                ['extract_user_email', 'Extracts the lead\'s email address from the conversation.'],
                ['register_new_user', 'Registers a first-time caller into the system (both GHL CRM and FDR Server).'],
                ['extract_user_registration_details', 'Extracts all details needed to register a new user (name, email, phone, state, debt, credit score).'],
                ['soft_credit_pull', 'Sends extracted lead address, DOB, and full name to the FDR Server to perform a soft credit pull, buyer assignment, and transfer initiation.'],
                ['disqualify_lead', 'Disqualifies the lead and removes them from all active workflows within GHL.'],
                ['tag_dnc', 'Tags the lead as Do Not Call and removes them from all workflows within GHL.'],
                ['national_debt_relief_live_transfer', 'Transfers the lead live to National Debt Relief.'],
                ['accredited_debt_relief_live_transfer', 'Transfers the lead live to Accredited Debt Relief.'],
                ['first_choice_debt_relief_live_transfer', 'Transfers the lead live to First Choice Debt Relief.'],
              ]
            }
          }
        ]
      },
      {
        title: 'Appendix C: API Key Management',
        content: 'All API keys and secrets for the following services are stored in the .env file on the Heroku-hosted FDR Server:',
        items: [
          'Retell AI',
          'Array (Soft Credit Pull)',
          'GoHighLevel (CRM webhooks & API)',
          'Buyer Partner APIs (National Debt Relief, Accredited Debt Relief, First Choice Debt Relief, Turbo Debt Relief)',
          'CustomerLabs',
          'TrustedForm',
        ]
      },
      {
        title: 'Appendix D: External Links & Assets',
        table: {
          headers: ['Resource', 'URL'],
          rows: [
            ['Landing Page — Long Format', 'https://www.finaldebtrelief.com/lf'],
            ['Landing Page — Qualification', 'https://www.finaldebtrelief.com/qp'],
            ['Landing Page — Video Sales Letter', 'https://www.finaldebtrelief.com/nvsl'],
            ['Array Form (Step 2 — Soft Credit Pull)', 'https://link.apisystem.tech/widget/form/8v9rO3Re44vdytWS9BWl'],
          ]
        }
      },
      {
        title: 'Appendix E: Lead Scoring Model (0–100+ Scale)',
        content: 'The FDR system employs a points-based lead scoring model to quantify lead quality in real time. Scores are calculated dynamically as leads progress through the funnel, and they directly influence prioritization, retry logic, and pipeline classification within GHL.',
        subsections: [
          {
            title: 'E.1 Behavioral Intent (Milestone Points)',
            content: 'These points track how far the lead has progressed through the funnel. Each milestone represents increasing commitment and intent.',
            table: {
              headers: ['Milestone', 'Tag', 'Points', 'Rationale'],
              rows: [
                ['Survey Submitted', '2fa_missed', '+5', 'Baseline entry — lead has expressed initial interest.'],
                ['OTP Verified', 'sms_optin', '+10', 'High intent — lead verified their phone number.'],
                ['SCP Attempted', 'soft_credit_pull_1', '+5', 'Ready to share personal data for credit check.'],
                ['SCP Success', 'submitted_array_form', '+25', 'CRITICAL milestone — lead has fully committed to the process.'],
              ]
            }
          },
          {
            title: 'E.2 Quality & Revenue Potential (Debt Amount)',
            content: 'The lead\'s declared or verified unsecured debt amount is the strongest indicator of revenue potential. Leads under $7,000 are automatically disqualified.',
            table: {
              headers: ['Debt Range', 'Points'],
              rows: [
                ['Under $7,000', 'DISQUALIFIED (−100)'],
                ['$7,000 – $10,000', '+5'],
                ['$10,000 – $20,000', '+10'],
                ['$20,000 – $30,000', '+15'],
                ['$30,000 – $40,000', '+20'],
                ['$40,000 – $50,000', '+25'],
                ['$50,000 – $75,000', '+35'],
                ['$75,000 – $100,000', '+45'],
                ['$100,000+', '+55'],
              ]
            }
          },
          {
            title: 'E.3 Post-Lead Engagement & Decay (Call Attempts)',
            content: 'Based on data analysis, lead value drops significantly after 3 failed call attempts. Successful pickups add points; repeated failures trigger rapid decay.',
            table: {
              headers: ['Event', 'Points'],
              rows: [
                ['Answers AI Call (1st attempt)', '+20'],
                ['Answers AI Call (2nd attempt)', '+10'],
                ['Answers AI Call (3rd attempt)', '+5'],
                ['Answers AI Call (4th attempt)', '+0'],
                ['Attempt 2 Fail', '−1'],
                ['Attempt 3 Fail', '−1'],
                ['Attempt 4+ Fail', '−25'],
                ['Email Open Rate 50% (Day 1–8, 5–6 Emails)', '+20'],
                ['Email Open Rate 33% (Day 1–8, 5–6 Emails)', '+15'],
                ['Email Open Rate 20% (Day 1–8, 5–6 Emails)', '+10'],
                ['Email Open Rate 15% (Day 1–8, 5–6 Emails)', '+5'],
              ]
            }
          },
          {
            title: 'E.4 Lead Tier Classification / GHL Pipeline',
            content: 'Scores map directly to lead tiers, which determine pipeline stage, retry priority, and human intervention rules within GHL.',
            table: {
              headers: ['Tier', 'Score', 'Profile'],
              rows: [
                ['Whale / Hot', '85+', '$30k+ debt, SCP Success, SMS Opt-in.'],
                ['High Value', '65–84', '$20k+ debt, SCP Success.'],
                ['Standard', '40–64', '$10k+ debt, SCP Success.'],
                ['Active Nurture', '15–39', 'SMS Opt-in done, but no debt data or no SCP yet.'],
                ['Dead / Unqualified', '< 0', 'Under $10k debt OR 5+ failed calls.'],
              ]
            }
          },
          {
            title: 'E.5 Whale Alert Protocol',
            content: `If a lead scores 85+, the system bypasses standard retry logic. These are the highest-value leads in the pipeline and warrant immediate, personalized human attention:

• If a Whale lead misses the first AI call, a human agent should manually attempt to save the lead with a personalized text message.
• Whale leads are the only tier where manual human outreach is triggered.
• The goal is to ensure no high-value lead is lost to automated retry decay.`
          }
        ]
      }
    ]
  },

  'V4': {
    label: 'V4 — API Transfer & Dynamic Buyer Routing',
    date: 'March 2026',
    summary: 'V4 replaces the Live Chat AI + Retell-for-all flow with immediate API transfer after soft credit pull. Qualified leads go directly to a Dynamic Buyer Page. Duplicate rejections fall back to the V3 Retell call flow. State rejections go to a Disqualification Page. New tags: api_transfer, duplicate_rejection.',
    sections: [
      {
        title: '1. Executive Summary',
        content: `The FDR Lead Processing System (Version 4) is designed to:

• Acquire high-intent leads via digital advertising (Meta, landing pages, direct calls)
• Qualify leads based on unsecured debt thresholds and creditworthiness
• Verify identity through Two-Factor Authentication (2FA) and soft credit pulls
• Route qualified leads autonomously to the appropriate debt relief partner via API transfer with dynamic buyer page delivery, or live call transfer
• Nurture leads who do not convert immediately through automated SMS, email, and outbound call retry workflows

Key Change in V4: The Live Chat AI component has been removed. After Array form submission and soft credit pull, qualified leads are now immediately routed to buyers via API transfer. Accepted leads are redirected to a Dynamic Buyer Page (DBP). Leads rejected as duplicates enter the V3-era results page and outbound call workflow. Leads rejected due to state restrictions are sent to a Disqualification Page.`
      },
      {
        title: '2.1 Technology Stack',
        table: {
          headers: ['Component', 'Function', 'Technology'],
          rows: [
            ['FDR Server', 'Custom "logic engine" managing buyer routing, API transfers, soft credit pulls, lead registration, tagging, and data logging.', 'Node.js (Express), TypeScript/JavaScript'],
            ['Heroku', 'Secure cloud hosting for the FDR backend server.', 'Heroku Cloud Platform'],
            ['ClickFunnels (CF)', 'Frontend web architecture for landing pages and results delivery.', 'ClickFunnels'],
            ['GoHighLevel (GHL)', 'Centralized CRM for survey hosting, lead database management, calendar bookings, pipeline tracking, and SMS/Email automations.', 'GoHighLevel'],
            ['Retell AI', 'Advanced conversational voice AI engine powering the AI Agent ("Jessica") across 3 bots.', 'Retell AI'],
            ['Array', 'Third-party API used to perform soft credit pulls; returns credit report data without requiring SSN.', 'Array API'],
            ['CustomerLabs', 'Cross-platform tracking and attribution software; tracks leads across ClickFunnels, GHL, and React/Server pages.', 'CustomerLabs CDP'],
          ]
        }
      },
      {
        title: '2.2 How Platforms Connect',
        content: 'The FDR Server acts as the central orchestration hub that bridges all platforms:',
        items: [
          'ClickFunnels → GHL: Landing page CTAs embed GHL survey forms. On submission, lead data flows into GHL\'s CRM.',
          'GHL → FDR Server: GHL automations trigger HTTP webhook requests to the FDR Server when specific events occur.',
          'FDR Server → Array: The server sends lead PII (name, address, DOB — not SSN) to the Array API and receives credit report data in return.',
          'FDR Server → Buyer API: After a successful soft credit pull, the server sends the lead to the buyer via API transfer. The buyer\'s API responds with accept/reject and a rejection reason if applicable.',
          'FDR Server → Dynamic Buyer Page: If the buyer accepts, the server redirects the lead to the appropriate Dynamic Buyer Page (over $20k or under $20k) with personalized query parameters.',
          'FDR Server → Retell AI: For leads rejected as duplicates, the server initiates outbound call requests to Retell AI and receives real-time call data back via custom function webhooks.',
          'FDR Server → GHL: The server writes tags, updates pipeline stages, registers calendar events, and triggers downstream automations back into GHL.',
          'CustomerLabs: Tracking code is deployed across all user-facing pages and listens for Google DataLayer events to stitch the full user journey.',
        ]
      },
      {
        title: '3. The Lead Journey — Full Lifecycle',
        subsections: [
          {
            title: 'Phase I: Capture & Segmentation',
            items: [
              'Traffic Entry: Leads arrive from digital advertisements (primarily Meta/Facebook) to one of three ClickFunnels landing pages: Long Format (LF), Qualification Page (QP), or Video Sales Letter (NVSL).',
              'Initial Qualification: CTA buttons trigger a GHL Survey embedded on the page.',
              'Bifurcation: The system automatically splits leads — Workflow A: Under $20,000. Workflow B: Over $20,000.',
            ]
          },
          {
            title: 'Phase II: Verification & Identity Confirmation',
            items: [
              'TCPA-Compliant 2FA (Step 1): Users complete phone verification. A one-time code is sent via SMS; successful completion applies the sms_optin tag.',
              'TrustedForm Certificate Generation: The system generates a unique TrustedForm Certificate for each lead, providing proof of consent.',
              'Array Form (Step 2): A secondary form is revealed requesting DOB, street address, city, and zip code. Submission triggers the FDR Server to perform a soft credit pull via the Array API. All forms are TCPA compliant.',
            ]
          },
          {
            title: 'Phase III: API Transfer & Dynamic Buyer Page Routing (NEW IN V4)',
            content: 'This phase replaces the V3 flow where leads were shown a results page with Live Chat AI and waited for an outbound Retell call.',
            items: [
              'Immediate API Transfer: Upon successful soft credit pull, the FDR Server immediately sends the qualified lead to the matched buyer via API transfer. The payload includes lead PII, credit data, TrustedForm certificate URL, and tracking parameters.',
              'Outcome A — Buyer Accepts: Lead is tagged api_transfer and redirected to the Dynamic Buyer Page (over20-dbp1 or under20-dbp1). The DBP displays the buyer\'s branding and next steps.',
              'Outcome B — Buyer Rejects (Duplicate Record): Lead is tagged duplicate_rejection and redirected to the Results Page. The V3 outbound call workflow activates: after a 60-second delay, GHL triggers the FDR Server to initiate an outbound Retell AI call.',
              'Outcome C — Buyer Rejects (State Not Serviced): Lead is tagged not_qualified and redirected to the Disqualification Page (dq-lf). No further outbound call attempts are made.',
            ]
          },
          {
            title: 'Phase IV: AI Engagement & Partner Routing (Duplicate Rejection Path Only)',
            items: [
              'Automated Trigger: After a 60-second latency period, GHL triggers the FDR Server, which initiates an outbound call request to Retell AI.',
              'Conversational AI ("Jessica"): The outbound bot confirms identity and submitted details, collects or verifies address/DOB, obtains consent, triggers a soft credit pull, and receives the matched buyer.',
              'Buyer Routing: The server identifies the appropriate partner and Jessica initiates a live warm transfer to the partner\'s call center.',
              'Fallback: If the lead does not answer, Jessica leaves a voicemail and the system enters the automated retry and nurturing workflows.',
            ]
          },
          {
            title: 'Alternative Entry Flows',
            items: [
              'Meta Form Leads: Leads generated directly from Meta lead forms are ingested into GHL and routed into the V4 automation pipeline at the appropriate entry point.',
              'Direct Inbound Calls: Leads who call +1 (855) 799-5699 (displayed on landing pages) are handled by the "Inbound Jessica for Landing Pages" bot.',
              'Inbound Callbacks: Leads returning calls to any of the 6 outbound numbers are handled by the "Inbound Jessica" bot.',
            ]
          }
        ]
      },
      {
        title: '4. Backend Server & Routing Engine',
        subsections: [
          {
            title: '4.1 Overview',
            content: 'The FDR Server is the proprietary logic engine at the heart of the system. It is a Node.js (Express) application written in TypeScript/JavaScript, hosted on Heroku.'
          },
          {
            title: '4.2 Core Server Responsibilities',
            table: {
              headers: ['Function', 'Description'],
              rows: [
                ['Lead Registration', 'Receives lead data from GHL webhooks and Retell custom functions; creates/updates lead records.'],
                ['Soft Credit Pull Orchestration', 'Sends lead PII to the Array API, receives and parses credit report data, and stores results.'],
                ['API Transfer to Buyer', 'Sends qualified lead data to the buyer\'s API immediately after soft credit pull. Processes the accept/reject response and routes the lead accordingly.'],
                ['Dynamic Buyer Page Routing', 'On buyer acceptance, redirects the lead to the appropriate DBP URL (over20-dbp1 or under20-dbp1) with full query parameters including contact ID, PII, tracking data, and buyer name.'],
                ['Disqualification Page Routing', 'On state-based rejection, redirects the lead to the disqualification page (dq-lf) with contact ID and name.'],
                ['Buyer Assignment', 'Applies business rules (debt amount, time of day, buyer availability, state) to assign the lead to the optimal debt relief partner.'],
                ['Live Transfer Initiation', 'For duplicate-rejected leads entering the Retell flow: returns the assigned buyer to Retell AI so the bot can execute a warm transfer.'],
                ['Tag Management', 'Writes and updates tags on lead records in GHL to reflect real-time status (e.g., api_transfer, duplicate_rejection, not_qualified, sent_to_buyer, warm_transfer).'],
                ['Call Scheduling', 'Receives scheduling requests from the Retell bot and registers them on the GHL calendar.'],
                ['DNC Processing', 'Processes Do-Not-Call requests by tagging the lead and removing them from all active GHL automations.'],
                ['Disqualification', 'Handles leads who fail qualification (e.g., cannot afford $250/month) by tagging and removing from workflows.'],
                ['Phone Number Routing', 'Implements state-based caller ID logic for outbound calls.'],
              ]
            }
          },
          {
            title: '4.3 API Key Management',
            content: 'All API keys for integrated services (Retell AI, Array, GoHighLevel, buyer partner APIs) are stored in a .env file on the Heroku server environment. Access is restricted to authorized developers with Heroku dashboard access.'
          }
        ]
      },
      {
        title: '5. Retell AI Bots — Inventory & Phone Numbers',
        subsections: [
          {
            title: '5.1 Bot Inventory',
            table: {
              headers: ['#', 'Bot Name', 'Type', 'Purpose'],
              rows: [
                ['1', 'Outbound Credit Check Agent Jessica', 'Outbound', 'Makes outbound calls to leads rejected as duplicates who are on the Results Page. Verifies details, performs soft credit pull, and transfers to a buyer. (V4 change: no longer called for all leads — only duplicate-rejected leads.)'],
                ['2', 'Inbound Jessica for Landing Pages', 'Inbound', 'Handles inbound calls from the number displayed on ClickFunnels landing pages. Supports both returning users and brand-new callers.'],
                ['3', 'Inbound Jessica', 'Inbound', 'Handles inbound callbacks to any of the 6 outbound phone numbers. Looks up the caller in the CRM and continues the qualification flow.'],
              ]
            }
          },
          {
            title: '5.2 Phone Numbers',
            table: {
              headers: ['Phone Number', 'Assigned State / Role', 'Purpose'],
              rows: [
                ['+1 (213) 955-7699', 'California (CA)', 'Outbound calls to CA leads'],
                ['+1 (330) 835-5076', 'Ohio (OH)', 'Outbound calls to OH leads'],
                ['+1 (904) 664-0193', 'Florida (FL)', 'Outbound calls to FL leads'],
                ['+1 (512) 367-5777', 'Texas (TX)', 'Outbound calls to TX leads'],
                ['+1 (773) 295-4374', 'Illinois (IL)', 'Outbound calls to IL leads'],
                ['+1 (855) 780-4538', 'General (Toll-Free)', 'Outbound calls to all other states; callback number in voicemails'],
                ['+1 (855) 799-5699', 'Landing Page Inbound (Bot 2)', 'Displayed on CF landing pages; handles first-time callers and returning users'],
                ['All 6 outbound numbers', 'Inbound Callbacks (Bot 3)', 'All 6 outbound numbers also accept inbound callbacks'],
              ]
            }
          }
        ]
      },
      {
        title: '6. Soft Credit Pull via Array',
        subsections: [
          {
            title: '6.1 What Array Does',
            content: 'Array is the third-party service that enables the system to perform a soft credit pull — a credit inquiry that does NOT affect the lead\'s credit score and does NOT require their SSN.'
          },
          {
            title: '6.2 Request Payload',
            items: ['First Name', 'Last Name', 'Full Address (Street, City, State, Zip Code)', 'Date of Birth', 'IP Address']
          },
          {
            title: '6.3 Response Data Fields',
            table: {
              headers: ['Field Code', 'Description'],
              rows: [
                ['creditScore', 'The lead\'s credit score'],
                ['PT146', 'Total balance on unsecured tradelines'],
                ['PT145', 'Total balance on unsecured credit card tradelines'],
                ['PT187', 'Total balances on currently delinquent tradelines'],
                ['PT184', 'Total number of trades ever delinquent'],
                ['PT132', 'Total amount on Collections'],
                ['PT019', 'Total Mortgage Monthly Payment'],
                ['PT141', 'Total occurrences of 30 days late within the past 12 months on Mortgage trades'],
                ['MT57S', 'Total past due amount of open mortgage trades verified in past 12 months'],
                ['MT36S', 'Months since most recent mortgage delinquency'],
                ['PT137', 'Total amount on Collections in past 36 months; excluding medical'],
                ['PT174', 'Total amount on Collections; excluding medical'],
                ['ST33S', 'Total balance of open student loan trades verified in past 12 months'],
                ['PT130', 'Number of Tax Liens'],
                ['PT147', 'Total amount on Tax Liens filed within the past 36 months'],
                ['PT175', 'Total amount on Tax Liens'],
                ['BC57S', 'Total past due amount of open credit card trades verified in past 12 months'],
                ['PT179', 'Total balance on open credit card trades'],
                ['PT182', 'Total number of open credit card trades'],
                ['PT205', 'Balance on unsecured personal finance accounts, including joint and authorized user accounts'],
              ]
            }
          },
          {
            title: '6.4 Retry Logic',
            content: 'The system allows up to 3 attempts for a soft credit pull. If the first attempt fails (e.g., data mismatch, API timeout), the lead is prompted to re-submit via a retry form. Tags soft_credit_pull_1, soft_credit_pull_2, and soft_credit_pull_3 track each attempt. After 3 failures, the tag soft_credit_pull_failed is applied.\n\nRetry Form URL: https://app.finaldebtrelief.com/fallback-form?dob={dob}&street={street}&city={city}&state={state}&zipcode={zip}&email={email}&phone={phone}&error={error}&contact_id={id}'
          },
          {
            title: '6.5 Key Qualification Thresholds',
            items: [
              'Qualified: Unsecured debt ≥ $7,000 — lead is routed to a buyer via API transfer.',
              'Not Qualified: Unsecured debt < $7,000 — lead receives the not_qualified tag; buyer returned as "False Buyer".',
              'Frozen Credit Report: If the report returns as "NA," the lead is tagged frozen_credit_report.',
            ]
          }
        ]
      },
      {
        title: '7. API Transfer & Dynamic Buyer Page Routing (NEW IN V4)',
        subsections: [
          {
            title: '7.1 API Transfer Flow',
            content: 'Once the soft credit pull returns qualified data (unsecured debt ≥ $7,000), the FDR Server immediately submits the lead to the matched buyer\'s API. The API transfer payload includes:',
            items: [
              'Lead PII (name, email, phone, address, DOB)',
              'Credit report data from Array',
              'TrustedForm certificate URL',
              'UTM tracking parameters and CustomerLabs CLUID',
              'Declared and verified debt amounts',
            ]
          },
          {
            title: '7.2 Buyer Response Handling',
            table: {
              headers: ['Response', 'Reason', 'Action', 'Tag Applied'],
              rows: [
                ['Accepted', 'N/A', 'Redirect to Dynamic Buyer Page (over20-dbp1 or under20-dbp1)', 'api_transfer'],
                ['Rejected', 'Duplicate Record', 'Redirect to Results Page; V3 outbound call workflow activates', 'duplicate_rejection'],
                ['Rejected', 'State Not Serviced', 'Redirect to Disqualification Page (dq-lf)', 'not_qualified'],
              ]
            }
          },
          {
            title: '7.3 Dynamic Buyer Page (DBP) Query Parameters',
            content: 'Over $20k DBP: https://www.finaldebtrelief.com/over20-dbp1\nUnder $20k DBP: https://www.finaldebtrelief.com/under20-dbp1',
            table: {
              headers: ['Parameter', 'Description'],
              rows: [
                ['contact_id', 'GHL contact ID'],
                ['first_name', 'Lead\'s first name'],
                ['email', 'Lead\'s email address'],
                ['phone', 'Lead\'s phone number (E.164 format)'],
                ['page', 'Source landing page identifier (e.g., lf, qp, nvsl)'],
                ['utm-content', 'UTM content parameter from ad'],
                ['utm-source', 'UTM source parameter from ad'],
                ['cluid', 'CustomerLabs unique identifier'],
                ['amount', 'Verified unsecured debt amount'],
                ['trusted_form_cert_url', 'TrustedForm certificate URL for compliance'],
                ['buyer', 'Accepted buyer\'s name (e.g., National Debt Relief)'],
              ]
            }
          },
          {
            title: '7.4 Disqualification Page',
            content: 'URL: https://www.finaldebtrelief.com/dq-lf\nQuery parameters: contact_id, first_name, last_name.\n\nThis page is shown when a buyer rejects the lead because they do not service the lead\'s state of residence. The page informs the user that services are not available in their area.'
          }
        ]
      },
      {
        title: '8. Tracking Architecture — CustomerLabs & DataLayer',
        subsections: [
          {
            title: '8.1 Why CustomerLabs',
            content: 'The FDR system spans 3 distinct platforms (ClickFunnels, GoHighLevel, and React/Server-rendered pages). CustomerLabs solves cross-domain attribution by deploying a unified tracking snippet across all pages and listening for Google DataLayer events to attribute the full journey — from ad click to buyer transfer — back to the original Meta campaign.'
          },
          {
            title: '8.2 CustomerLabs Tracking Code',
            content: 'The following snippet is deployed on every user-facing page (same as V3):',
            code: `<!-- CustomerLabs Tag -->
<script>
!function(t,e,r,c,a,n,s){t.ClAnalyticsObject=a,t[a]=t[a]||[],
t[a].methods=["trackSubmit","trackClick","pageview","identify","track","trackConsent"],
t[a].factory=function(e){return function(){var r=Array.prototype.slice.call(arguments);
return r.unshift(e),t[a].push(r),t[a]}};for(var i=0;i<t[a].methods.length;i++){
var o=t[a].methods[i];t[a][o]=t[a].factory(o)};n=e.createElement(r),
s=e.getElementsByTagName(r)[0],n.async=1,n.crossOrigin="anonymous",
n.src=c,s.parentNode.insertBefore(n,s)}
(window,document,"script","https://cdn.js.customerlabs.co/cl79026oknmil0.js","_cl");
_cl.SNIPPET_VERSION="2.0.0"
</script>
<!-- End of CustomerLabs Tag -->`
          },
          {
            title: '8.3 Event Tracking Map',
            content: 'V4 change: "Purchase" event now fires on the Dynamic Buyer Pages (over20-dbp1, under20-dbp1) for accepted leads, in addition to the results page for duplicate-path leads. "Unqualified" now also fires on /dq-lf (state rejection).',
            table: {
              headers: ['Event Name', 'Trigger', 'Pages / Forms', 'DataLayer Payload'],
              rows: [
                ['View_content', 'Lead lands on a ClickFunnels page', '/lf1, /qp1, /nvsl1', 'event, url'],
                ['Initiate_checkout', 'Lead opens/interacts with the GHL survey form', 'GHL form embeds (2 form variants)', 'event, first_name, last_name, email, phone'],
                ['Add_to_cart', 'Lead completes 2FA and reaches the validating-data bridge page', '/validating-data', 'event, url'],
                ['Purchase', 'Lead is accepted by buyer (DBP) or reaches results page (duplicate path)', '/over20-dbp1, /under20-dbp1, /over20-result-re1, /under20-result-re1', 'event, first_name, last_name, email, phone, currency ("usd"), value (debt amount)'],
                ['Unqualified', 'Lead is disqualified (debt below threshold or state not serviced)', '/ddq-lf1, /dq-lf', 'event, first_name, last_name, email, phone'],
                ['Contact', 'Lead lands on the "no credit data" page (frozen report or pull failure)', '/no-cred-re1', 'event, first_name, last_name, email, phone'],
              ]
            }
          },
          {
            title: '8.4 DataLayer Code Snippets',
            code: `// View_content
window.dataLayer.push({ event: "View_content", url: window.location.href });

// Initiate_checkout
window.dataLayer.push({ event: "Initiate_checkout", first_name, last_name, email, phone });

// Add_to_cart
window.dataLayer.push({ event: "Add_to_cart", url: window.location.href });

// Purchase
window.dataLayer.push({ event: "Purchase", first_name, last_name, email, phone, currency: "usd", value: amount });

// Unqualified
window.dataLayer.push({ event: "Unqualified", first_name, last_name, email, phone });

// Contact
window.dataLayer.push({ event: "Contact", first_name, last_name, email, phone });`
          }
        ]
      },
      {
        title: '9. System Tagging & Logic',
        subsections: [
          {
            title: '9.1 Progress & Recovery Tags',
            table: {
              headers: ['Tag Name', 'Trigger Event', 'Downstream Workflows'],
              rows: [
                ['2fa_missed', 'Lead submits the initial survey but does not complete the 2FA verification form.', 'V3 - Retell - Ai Outbound Attempts (Calls), V3 - Retell - FDR Nurturing Email, V3 - Retell - Ai Agent Secondary Workflow (SMS)'],
                ['sms_optin', 'Lead successfully completes the 2FA form. Lead retains this tag until the Array Form is submitted.', 'V3 - Retell - Ai Outbound Attempts (Calls), V3 - Retell - FDR Nurturing Email, V3 - Retell - Ai Agent Secondary Workflow (SMS)'],
                ['submitted_array_form', 'Lead completes the Array Form (Address, DOB, Zip). Triggers immediate API transfer flow.', 'API Transfer → DBP (if accepted), Results Page + V3 - Retell - Ai Outbound Attempts (if duplicate), DQ Page (if state rejected)'],
              ]
            }
          },
          {
            title: '9.2 Credit Analysis Tags',
            table: {
              headers: ['Tag Name', 'Description'],
              rows: [
                ['soft_credit_pull_1', 'Applied when a soft credit pull is performed for the first time.'],
                ['soft_credit_pull_2', 'Applied if a second pull is performed due to a failure or error in the first attempt.'],
                ['soft_credit_pull_3', 'Applied if a third pull is performed due to a failure or error in the second attempt.'],
                ['soft_credit_pull_failed', 'Applied when the soft credit pull fails to return data after 3 attempts.'],
                ['soft_credit_pull_empty_cart', 'Applied if an error occurs during attempt 1 or 2 and the lead exits the system before attempting a 3rd time.'],
              ]
            }
          },
          {
            title: '9.3 Qualification, Intelligence & Routing Tags',
            table: {
              headers: ['Tag Name', 'Description'],
              rows: [
                ['not_qualified', 'Applied in two scenarios: (1) soft credit pull reveals unsecured debt is under $7,000 — buyer returned as "False Buyer"; (2) buyer rejects because they do not service the lead\'s state — lead sent to Disqualification Page.'],
                ['frozen_credit_report', 'Applied when the credit report returns as "NA," indicating the lead has a frozen credit report.'],
                ['api_transfer', 'NEW IN V4. Applied when the lead is accepted by the buyer via API transfer and redirected to the Dynamic Buyer Page.'],
                ['duplicate_rejection', 'NEW IN V4. Applied when the buyer rejects the lead due to a duplicate record. Lead enters the Results Page and outbound call flow.'],
                ['sent_to_buyer', 'Applied when the lead is successfully transferred to a buyer (via warm transfer in the duplicate/Retell path).'],
                ['warm_transfer', 'Applied when the lead was transferred to the buyer live over the call with Jessica.'],
                ['retell_outbound_attempts_1 through n+1', 'Applied sequentially each time a lead is called.'],
                ['retell_after_hrs', 'Applied if a lead opts in after 11:45 PM and before 8:00 AM EST.'],
              ]
            }
          }
        ]
      },
      {
        title: '10. Automation & Exception Handling',
        content: 'The system employs robust automated workflows within GHL to handle scenarios where the "Happy Path" (immediate API transfer and buyer acceptance) does not occur.',
        table: {
          headers: ['Automation Name', 'Function'],
          rows: [
            ['V3 - Retell 1.0', 'First touch point. Determines the appropriate downstream automation to route the lead to.'],
            ['V3 - Retell - 2fa missed tagging', 'Tags leads who submitted the survey but missed the 2FA step.'],
            ['V3 - Retell - AI Outbound Attempts', 'Retries leads (duplicate-rejected path only) with outbound calls for the next 30 days.'],
            ['V3 - Retell - After hrs leads comm.', 'Handles communication with leads who enter the system after 11:45 PM EST.'],
            ['V3 - Retell - Agent Schedule a call over call for users', 'Processes call-back scheduling requests made by the lead during a Retell call.'],
            ['V3 - Retell - Ai Agent Call secondary workflow', 'Nurturing SMS flow for 2fa_missed and sms_optin leads who have not completed the credit pull.'],
            ['V3 - Retell - Ai Phone call calendar booked', 'Handles SMS reminders for leads who booked a callback, and initiates the call at the booked time.'],
            ['V3 - Remove all', 'Removes leads from all active automations after the lead is successfully sent to a buyer.'],
            ['V3 - Retell - Array form submitted tagging', 'Tags leads who successfully submitted the Array form.'],
            ['v3 - Retell - 2fa missed lead integration', 'Adds 2fa_missed leads to nurturing SMS and email flows.'],
            ['V3 - Retell Call Status Update', 'Updates call status records from Retell in GHL.'],
            ['V3 - [FDR] Update Opportunity Final', 'Updates the lead\'s position in the GHL pipeline.'],
            ['v3 Retell - Frozen credit report tagging', 'Tags leads with a frozen credit report.'],
            ['v3 Retell - sms optin tagging', 'Tags leads who complete the OTP/2FA verification.'],
            ['V3 - Retell - Soft credit pull trial tagging', 'Tags leads with multiple Array form submission attempts.'],
            ['V3 - Retell - Call to Complete Soft Credit Pull', 'Adds leads who didn\'t submit the Array form to V3 - Retell 1.0.'],
            ['v3 - Retell - Failed Array form empty cart', 'Adds leads who abandon the Array retry form to V3 - Retell 1.0.'],
            ['V3 - Retell - AI Outbound Attempts bucket 1-3 pt 2', 'Manages continuous outbound attempts based on the lead\'s time-zone bucket.'],
            ['V3 - Retell - Tag Lead When They Answer A Call', 'Tags the lead as "answered" when generated by the FDR server.'],
            ['V3 - Retell - Trigger Server To Create Tag For Answered Calls', 'Sends updated tags for answered calls to the server.'],
          ]
        }
      },
      {
        title: '11. System Flow Diagrams',
        subsections: [
          {
            title: '11.1 Primary Lead Journey — Happy Path (V4)',
            diagram: `Ad Click
→ ClickFunnels Landing Page (LF / QP / NVSL)
→ GHL Survey Submission [tag: 2fa_missed]
→ 2FA Phone Verification [tag: sms_optin]
→ Array Form (DOB, Address, Zip) [tags: submitted_array_form, soft_credit_pull_1]
→ FDR Server performs Soft Credit Pull via Array
→ FDR Server sends lead to Buyer via API Transfer
→ Buyer Accepts [tag: api_transfer]
→ Lead redirected to Dynamic Buyer Page (over20-dbp1 or under20-dbp1)`
          },
          {
            title: '11.2 Duplicate Rejection Path',
            diagram: `... (Array Form submitted, Soft Credit Pull successful)
→ FDR Server sends lead to Buyer via API Transfer
→ Buyer Rejects — Duplicate Record [tag: duplicate_rejection]
→ Lead redirected to Results Page
→ GHL Trigger (60s delay)
→ FDR Server initiates Retell AI Outbound Call [tag: retell_outbound_attempt_1]
→ Jessica verifies details on call
→ Buyer Assignment by Server
→ Live Warm Transfer to Buyer [tags: sent_to_buyer, warm_transfer]`
          },
          {
            title: '11.3 State Not Serviced Path',
            diagram: `... (Array Form submitted, Soft Credit Pull successful)
→ FDR Server sends lead to Buyer via API Transfer
→ Buyer Rejects — State Not Serviced [tag: not_qualified]
→ Lead redirected to Disqualification Page (dq-lf)
→ No further outbound attempts`
          },
          {
            title: '11.4 Fallback & Recovery Paths',
            diagram: `If Lead Does Not Answer Call (Duplicate Path):
→ Jessica leaves voicemail
→ Lead enters V3 - Retell - AI Outbound Attempts (retries over 30 days)
→ Tags increment: retell_outbound_attempt_2, _3, ... _n

If Lead Requests Callback:
→ Jessica triggers schedule_call function
→ GHL calendar event created
→ V3 - Retell - Ai Phone call calendar booked sends SMS reminder
→ Call initiated at booked time

If Lead Enters After Hours (11:45 PM – 8:00 AM EST):
→ Tag: retell_after_hrs
→ V3 - Retell - After hrs leads comm. handles messaging
→ Call scheduled for next available window

If Soft Credit Pull Fails:
→ Retry up to 3 times [tags: soft_credit_pull_1, soft_credit_pull_2, soft_credit_pull_3]
→ If all fail: tag soft_credit_pull_failed
→ If lead abandons before 3rd attempt: tag soft_credit_pull_empty_cart

If Lead Not Qualified (debt < $7,000):
→ Buyer returned as "False Buyer" | Tag: not_qualified
→ Jessica informs lead; call ends gracefully

If Lead Requests DNC:
→ Jessica confirms opt-out → tag_dnc → Lead removed from all workflows

If Warm Transfer Fails After 30 Attempts (Duplicate Path):
→ Lead transferred via API handoff | Tag: api_transfer`
          },
          {
            title: '11.5 Inbound Call Flow (Landing Page)',
            diagram: `Lead calls +1 (855) 799-5699 from landing page
→ Inbound Jessica for Landing Pages answers

If Returning User:
  → Lookup by phone or email via FDR Server
  → If found: proceed to Qualification & Verification
  → If not found: treat as New User

If New User:
  → Collect: first name, last name, email, phone, state, debt amount, credit score
  → Trigger register_new_user
  → Proceed to Qualification & Verification

Qualification & Verification:
  → $250/month check → Consent → Collect DOB, Address, City, Zip
  → Trigger Soft Credit Pull → Buyer assignment & live transfer (or graceful exit)`
          },
          {
            title: '11.6 Inbound Callback Flow',
            diagram: `Lead calls back one of the 6 outbound numbers
→ Inbound Jessica answers
→ Lookup by phone or email via FDR Server
→ If found: proceed to Qualification & Verification
→ If not found: New User flow (collect details, register)
→ Same Qualification & Transfer flow as 11.5`
          }
        ]
      },
      {
        title: 'Appendix A: Retell Bot Prompts',
        subsections: [
          {
            title: 'A.1 — Outbound Credit Check Agent Jessica (Bot 1)',
            content: 'Type: Outbound | Purpose (V4): Call leads rejected as duplicates who are on the Results Page. Verify details, perform soft credit pull, and transfer to a matched buyer.',
            code: `AGENT ROLE & OBJECTIVE
You are Jessica, an empathetic and compliant Debt Relief Virtual Assistant working for Final Debt Relief (FDR). Your mission: perform outbound calls to leads who submitted their info, verify their submitted details, verify their identity through a soft credit check, then transfer them live to a debt relief specialist.

Always start the call: "I'm reaching out because based on the information you provided, you are eligible for a debt relief review. Congratulations — that's a big first step." Wait for response. "This call is just to confirm a few details so I can connect you with a debt relief specialist who will walk you through your personalized options."

SOFT CREDIT PULL FLOW
1. Confirm Identity: "Your full name on the application is {{name}}, is that correct?"
2. Confirm Debt Amount: "And I have your unsecured debt listed as {{debt_amount}} — is that correct?"
3. Monthly Ability Check: "Most programs require a minimum payment of at least $250 per month. Is that something you're able to manage comfortably?" (If NO → trigger disqualify_lead)
4. Consent Script: "I just need to quickly verify your identity and confirm your unsecured debt amount. This doesn't affect your credit score in any way. Do I have your permission?"

Validate email → Validate phone → Collect/validate DOB (YYYY-MM-DD) → Collect/validate street address, city, zip.
Trigger extract_user_details. IT IS MANDATORY to verify all fields before triggering the soft credit pull.

Trigger "Soft credit pull". While waiting (~10s): "I'm just checking your details in the system."
If buyer returned → "Great news — you qualify. I'm connecting you with a debt consolidation specialist from {{buyer}}."
If buyer = "False Buyer" → "We're unable to enroll you at this time."
If buyer = null → "We weren't able to match your profile right now — someone will follow up shortly."

VOICEMAIL DETECTION: If you hear "Please leave a message...", "Mailbox...", long silence + beep, or a greeting that never pauses → assume voicemail. Use voicemail script.

APPLE CALL SCREENING: If you hear "If you record your name and reason for calling..." → say: "It's Jessica from Final Debt Relief — I have those lower monthly numbers you requested. I'm ready to lock them in for you now." Then wait silently.

VOICEMAIL SCRIPT (exact): "Hi, this is Final Debt Relief calling about your recent request for help with unsecured debt. Please call us back at 855-780-4538 at your earliest convenience. Again, 855-780-4538. Thank you."

SCHEDULING: If unavailable → get_current_time → ask for callback time. Assume 5PM–8:30PM if no time given. Allowed hours: 8AM–11PM. Run schedule_call.

DNC: If user wants to stop calls → tag_dnc, thank them, end call.`
          },
          {
            title: 'A.2 — Inbound Jessica for Landing Pages (Bot 2)',
            content: 'Type: Inbound | Purpose: Handle inbound calls from the landing page phone number. Support both first-time callers and returning users.',
            code: `CALL OPENING: "Thank you for calling Final Debt Relief — this is Jessica. Who am I speaking with?"
"Are you calling for the first time, or have you already submitted a debt relief review on our website before?"

If returning user → look up by phone (get_user_by_phone, prepend "+1") or email (get_user_by_email).
If new user → collect: first name, last name, email, phone, state (2-letter), debt amount, credit score (numeric only). Trigger register_new_user.

QUALIFICATION & VERIFICATION (Both Paths):
1. $250/month check. If NO → trigger disqualify_lead.
2. Consent: "I just need to quickly verify your identity. This won't affect your credit score. Do I have your permission?"
3. Collect one at a time: email (spell letter by letter), phone, DOB (YYYY-MM-DD), street address, city and zip.
Trigger Soft Credit Pull.

CALL CLOSING:
Success → "Great news, you're pre-qualified! You've been matched with {{buyer}}. I'm connecting you with a live debt relief specialist."
False Buyer → "Our programs require more than $7,000 in unsecured debt. We're unable to enroll you at this time."
No match → "We weren't able to match your profile right now — someone will follow up shortly."

RULES: Always say "debt relief specialist." Never imply you dialed them. No financial/legal advice.`
          },
          {
            title: 'A.3 — Inbound Jessica (Bot 3)',
            content: 'Type: Inbound | Purpose: Handle inbound callbacks to outbound phone numbers. Look up callers in the CRM and continue qualification.',
            code: `CALL OPENING: "Thank you for calling Final Debt Relief — this is Jessica. Who am I speaking with?"
"Let me pull up your file — may I have the phone number you used on your application?"
CRITICAL: This is INBOUND. Never say "I'm calling because..." or imply you dialed them.

IF FOUND IN SYSTEM:
"I see you're eligible for a debt relief review. Let's confirm a few details so I can connect you with a specialist."
$250/month check → Consent → Collect email, phone, DOB (YYYY-MM-DD), street address, city and zip.
Trigger Soft Credit Pull.

IF NOT FOUND (NEW USER):
"I'm going to need a few details from you to proceed, is that ok?"
Collect: first name, last name, email, phone, state (2-letter), debt amount, credit score (numeric only).
Trigger register_new_user → $250 check → consent → detail collection → soft credit pull.

CALL CLOSING: Same as Bot 2.
FAIL-SAFE RULES: Never guess provider name. Never quote savings percentages. Never mention credit impact, taxes, lawsuits, or legal topics.`
          }
        ]
      },
      {
        title: 'Appendix B: Retell Custom Functions Reference',
        subsections: [
          {
            title: 'B.1 — Outbound Credit Check Agent Jessica (Bot 1)',
            table: {
              headers: ['Function Name', 'Purpose'],
              rows: [
                ['extract_user_details', 'Extracts and stores the lead\'s verified details (email, phone, DOB, address, city, zip) from the conversation.'],
                ['schedule_call', 'Sends the lead\'s preferred callback date/time to the FDR Server to register on the GHL calendar.'],
                ['get_current_time', 'Retrieves the current date and time (ISO format) for callback scheduling.'],
                ['soft_credit_pull', 'Sends extracted lead address, DOB, and full name to the FDR Server to perform a soft credit pull, buyer assignment, and transfer initiation.'],
                ['disqualify_lead', 'Disqualifies the lead and removes them from all active workflows within GHL.'],
                ['tag_dnc', 'Tags the lead as Do Not Call and removes them from all workflows within GHL.'],
                ['national_debt_relief_live_transfer', 'Transfers the lead live to National Debt Relief\'s call center.'],
                ['accredited_debt_relief_live_transfer', 'Transfers the lead live to Accredited Debt Relief\'s call center.'],
                ['first_choice_debt_relief_live_transfer', 'Transfers the lead live to First Choice Debt Relief\'s call center.'],
              ]
            }
          },
          {
            title: 'B.2 — Inbound Jessica for Landing Pages (Bot 2) & Inbound Jessica (Bot 3)',
            content: 'Both inbound bots share the same function set:',
            table: {
              headers: ['Function Name', 'Purpose'],
              rows: [
                ['get_user_by_email', 'Retrieves the lead\'s details from the FDR Server using their email address.'],
                ['extract_user_phone', 'Extracts the lead\'s phone number from the conversation.'],
                ['get_user_by_phone', 'Retrieves the lead\'s details from the FDR Server using their phone number.'],
                ['extract_user_email', 'Extracts the lead\'s email address from the conversation.'],
                ['register_new_user', 'Registers a first-time caller into the system (both GHL CRM and FDR Server).'],
                ['extract_user_registration_details', 'Extracts all details needed to register a new user (name, email, phone, state, debt, credit score).'],
                ['soft_credit_pull', 'Sends extracted lead address, DOB, and full name to the FDR Server to perform a soft credit pull, buyer assignment, and transfer initiation.'],
                ['disqualify_lead', 'Disqualifies the lead and removes them from all active workflows within GHL.'],
                ['tag_dnc', 'Tags the lead as Do Not Call and removes them from all workflows within GHL.'],
                ['national_debt_relief_live_transfer', 'Transfers the lead live to National Debt Relief.'],
                ['accredited_debt_relief_live_transfer', 'Transfers the lead live to Accredited Debt Relief.'],
                ['first_choice_debt_relief_live_transfer', 'Transfers the lead live to First Choice Debt Relief.'],
              ]
            }
          }
        ]
      },
      {
        title: 'Appendix C: API Key Management',
        content: 'All API keys and secrets for the following services are stored in the .env file on the Heroku-hosted FDR Server:',
        items: [
          'Retell AI',
          'Array (Soft Credit Pull)',
          'GoHighLevel (CRM webhooks & API)',
          'Buyer Partner APIs (National Debt Relief, Accredited Debt Relief, First Choice Debt Relief, Turbo Debt Relief)',
          'CustomerLabs',
          'TrustedForm',
        ]
      },
      {
        title: 'Appendix D: External Links & Assets',
        table: {
          headers: ['Resource', 'URL'],
          rows: [
            ['Landing Page — Long Format', 'https://www.finaldebtrelief.com/lf'],
            ['Landing Page — Qualification', 'https://www.finaldebtrelief.com/qp'],
            ['Landing Page — Video Sales Letter', 'https://www.finaldebtrelief.com/nvsl'],
            ['Array Form (Step 2)', 'https://link.apisystem.tech/widget/form/8v9rO3Re44vdytWS9BWl'],
            ['Dynamic Buyer Page (Over $20k)', 'https://www.finaldebtrelief.com/over20-dbp1'],
            ['Dynamic Buyer Page (Under $20k)', 'https://www.finaldebtrelief.com/under20-dbp1'],
            ['Disqualification Page', 'https://www.finaldebtrelief.com/dq-lf'],
          ]
        }
      },
      {
        title: 'Appendix E: Lead Scoring Model (0–100+ Scale)',
        content: 'Same scoring model as V3. Scores are calculated dynamically as leads progress through the funnel.',
        subsections: [
          {
            title: 'E.1 Behavioral Intent',
            table: {
              headers: ['Milestone', 'Tag', 'Points', 'Rationale'],
              rows: [
                ['Survey Submitted', '2fa_missed', '+5', 'Baseline entry — lead has expressed initial interest.'],
                ['OTP Verified', 'sms_optin', '+10', 'High intent — lead verified their phone number.'],
                ['SCP Attempted', 'soft_credit_pull_1', '+5', 'Ready to share personal data for credit check.'],
                ['SCP Success', 'submitted_array_form', '+25', 'CRITICAL milestone — lead has fully committed to the process.'],
              ]
            }
          },
          {
            title: 'E.2 Quality & Revenue Potential (Debt Amount)',
            table: {
              headers: ['Debt Range', 'Points'],
              rows: [
                ['Under $7,000', 'DISQUALIFIED (−100)'],
                ['$7,000 – $10,000', '+5'],
                ['$10,000 – $20,000', '+10'],
                ['$20,000 – $30,000', '+15'],
                ['$30,000 – $40,000', '+20'],
                ['$40,000 – $50,000', '+25'],
                ['$50,000 – $75,000', '+35'],
                ['$75,000 – $100,000', '+45'],
                ['$100,000+', '+55'],
              ]
            }
          },
          {
            title: 'E.3 Post-Lead Engagement & Decay',
            table: {
              headers: ['Event', 'Points'],
              rows: [
                ['Answers AI Call (1st attempt)', '+20'],
                ['Answers AI Call (2nd attempt)', '+10'],
                ['Answers AI Call (3rd attempt)', '+5'],
                ['Answers AI Call (4th attempt)', '+0'],
                ['Attempt 2 Fail', '−1'],
                ['Attempt 3 Fail', '−1'],
                ['Attempt 4+ Fail', '−25'],
                ['Email Open Rate 50% (Day 1–8)', '+20'],
                ['Email Open Rate 33% (Day 1–8)', '+15'],
                ['Email Open Rate 20% (Day 1–8)', '+10'],
                ['Email Open Rate 15% (Day 1–8)', '+5'],
              ]
            }
          },
          {
            title: 'E.4 Lead Tier Classification',
            table: {
              headers: ['Tier', 'Score', 'Profile'],
              rows: [
                ['Whale / Hot', '85+', '$30k+ debt, SCP Success, SMS Opt-in.'],
                ['High Value', '65–84', '$20k+ debt, SCP Success.'],
                ['Standard', '40–64', '$10k+ debt, SCP Success.'],
                ['Active Nurture', '15–39', 'SMS Opt-in done, but no debt data or no SCP yet.'],
                ['Dead / Unqualified', '< 0', 'Under $10k debt OR 5+ failed calls.'],
              ]
            }
          },
          {
            title: 'E.5 Whale Alert Protocol',
            content: 'If a lead scores 85+, the system bypasses standard retry logic:\n\n• If a Whale lead misses the first AI call, a human agent should manually attempt to save the lead with a personalized text message.\n• Whale leads are the only tier where manual human outreach is triggered.\n• The goal is to ensure no high-value lead is lost to automated retry decay.'
          }
        ]
      },
      {
        title: 'Appendix F: V3 → V4 Changelog',
        table: {
          headers: ['Area', 'V3 Behavior', 'V4 Behavior'],
          rows: [
            ['Live Chat AI', 'Active on Results Pages to answer questions and maintain engagement. Tagged leads as "chat".', 'Removed entirely. No Live Chat AI component in V4.'],
            ['Post-Array Form Flow', 'Lead sent to Results Page. After 60s delay, Retell outbound call initiated for all leads.', 'Lead immediately sent to buyer via API transfer. Three possible outcomes: accepted (DBP), duplicate rejected (Results Page + Retell flow), or state rejected (DQ Page).'],
            ['Buyer Acceptance', 'Buyer matched during Retell call. Live warm transfer attempted on the phone.', 'Buyer matched immediately after soft credit pull. Lead redirected to Dynamic Buyer Page with full query parameters.'],
            ['Duplicate Handling', 'Not explicitly handled as a distinct path.', 'Buyer rejects with "duplicate" reason. Lead sent to Results Page and enters V3-era Retell outbound call workflow.'],
            ['State Rejection', 'Not explicitly handled as a distinct path.', 'Buyer rejects with "state not serviced" reason. Lead tagged not_qualified and sent to Disqualification Page (dq-lf). No further outbound attempts.'],
            ['New Tags', 'N/A', 'api_transfer (on buyer acceptance), duplicate_rejection (on duplicate rejection). The existing not_qualified tag is now also applied for state-based rejections.'],
            ['Removed Tags', 'chat (Live Chat AI engagement tag)', 'Removed — no longer applicable.'],
            ['New Pages', 'N/A', 'Dynamic Buyer Pages (over20-dbp1, under20-dbp1) and Disqualification Page (dq-lf).'],
            ['Retell Bot 1 Scope', 'Called all leads who submitted Array form after 60s delay.', 'Only called for leads rejected as duplicates who land on the Results Page.'],
          ]
        }
      }
    ]
  },

  'V4.1': {
    label: 'V4.1 — Current Version',
    date: '2026 (Current)',
    summary: 'V4.1 is an incremental update to V4. The core architecture (API transfer, Dynamic Buyer Pages, duplicate/state-rejection paths) is unchanged. This version introduces 6 targeted improvements: sms_optin drop-offs routed to NDR, round-robin phone rotation for all states, after-hours leads sent via API transfer before the 5th call attempt, a new api_transfer_failed tag, warm-transfer calling for NDR API-transferred leads, and an extended API transfer window (8am–11:30 PM EST).',
    sections: [
      {
        title: 'V4 → V4.1 Changelog',
        table: {
          headers: ['#', 'Area', 'V4 Behavior', 'V4.1 Behavior'],
          rows: [
            ['1', 'sms_optin Drop-off Routing', 'Leads who completed 2FA but never submitted the Array form received SMS, Emails and Outbound calls nurturing.', 'These leads are now sent to NDR by the "V3 - Retell - Call to Complete Soft Credit Pull" workflow.'],
            ['2', 'Outbound Phone Number Rotation', 'State-specific numbers (CA, OH, FL, TX, IL) were assigned to leads from those states; other states got the toll-free number.', 'All leads regardless of state have their call numbers round-robined across all 6 numbers.'],
            ['3', 'After-Hours Lead Buyer Routing', 'After-hours leads (retell_after_hrs) received only outbound call retries; no API transfer was triggered for these leads.', 'After-hours leads are now sent to the buyer via API transfer before the 5th outbound call attempt (i.e., after 4 failed call attempts).'],
            ['4', 'API Transfer Failure Handling', 'No dedicated tag for API transfer failures.', 'Leads whose API transfer attempt fails are now tagged api_transfer_failed.'],
            ['5', 'Post-API-Transfer Calling (NDR)', 'No outbound calls were made after a lead was API-transferred.', 'Leads API-transferred to NDR are now called with warm transfer attempts. Calling stops once NDR\'s dashboard shows they have started contacting the lead.'],
            ['6', 'API Transfer Timeframe', 'Leads submitted between 8:00 AM – 9:30 PM EST were eligible for immediate API transfer.', 'The window is extended: leads submitted between 8:00 AM – 11:30 PM EST are now eligible for immediate API transfer.'],
          ]
        }
      },
      {
        title: '1. sms_optin Drop-off Routing to NDR',
        content: 'In V4, leads who completed the 2FA verification (earning the sms_optin tag) but never submitted the Array form would only receive automated SMS/email nurturing and outbound call retries.\n\nIn V4.1, the "V3 - Retell - Call to Complete Soft Credit Pull" automation now routes these drop-off leads directly to NDR (National Debt Relief) instead of relying solely on retry nurturing.',
        subsections: [
          {
            title: 'Lead State at This Point',
            items: [
              'Tag: sms_optin (2FA completed)',
              'No submitted_array_form tag (Array form was not submitted)',
              'No soft credit pull has been performed',
            ]
          },
          {
            title: 'V4.1 Behavior',
            items: [
              'Automation "V3 - Retell - Call to Complete Soft Credit Pull" now sends the lead to NDR.',
              'This applies to leads who dropped off after 2FA but before the Array form submission.',
            ]
          }
        ]
      },
      {
        title: '2. Round Robin Phone Numbers for All States',
        content: 'In V4, the system used state-specific caller IDs to maximize pickup rates: leads from CA, OH, FL, TX, and IL received calls from their state\'s dedicated number. All other states used the general toll-free number (+1 (855) 780-4538).\n\nIn V4.1, this logic is removed. All leads — regardless of their state — now have their outbound call numbers assigned via round-robin rotation across all 6 numbers.',
        subsections: [
          {
            title: 'V4 Phone Routing (Replaced)',
            table: {
              headers: ['State', 'V4 Number'],
              rows: [
                ['California (CA)', '+1 (213) 955-7699'],
                ['Ohio (OH)', '+1 (330) 835-5076'],
                ['Florida (FL)', '+1 (904) 664-0193'],
                ['Texas (TX)', '+1 (512) 367-5777'],
                ['Illinois (IL)', '+1 (773) 295-4374'],
                ['All other states', '+1 (855) 780-4538'],
              ]
            }
          },
          {
            title: 'V4.1 Phone Routing (Current)',
            content: 'All 6 numbers are rotated via round-robin for every lead, regardless of state:',
            items: [
              '+1 (213) 955-7699',
              '+1 (330) 835-5076',
              '+1 (904) 664-0193',
              '+1 (512) 367-5777',
              '+1 (773) 295-4374',
              '+1 (855) 780-4538',
            ]
          }
        ]
      },
      {
        title: '3. After-Hours Leads Sent to Buyer After 4 Outbound Calls',
        content: 'After-hours leads are leads who registered into the system after working hours (after 11:30 PM EST in V4.1, previously 9:30 PM). Because the API transfer window is closed, these leads do not get an immediate API transfer upon Array form submission — they instead enter the outbound call retry flow.',
        subsections: [
          {
            title: 'V4 Behavior (Replaced)',
            items: [
              'After-hours leads received only outbound call retries over 30 days.',
              'No API transfer was ever triggered for these leads.',
            ]
          },
          {
            title: 'V4.1 Behavior (Current)',
            items: [
              'After 4 failed outbound call attempts, the system triggers an API transfer to the matched buyer — before the 5th attempt is made.',
              'Tag: retell_after_hrs is still applied on entry.',
              'If the API transfer succeeds: lead is tagged api_transfer and outbound retries stop.',
              'If the API transfer fails: lead is tagged api_transfer_failed and retries continue.',
            ]
          },
          {
            title: 'Trigger Point',
            diagram: `Lead registers after 11:30 PM EST [tag: retell_after_hrs]
→ Outbound calls begin (retell_outbound_attempt_1 through _4)
→ All 4 attempts fail to get an answer
→ Before attempt 5: FDR Server triggers API transfer to buyer
  → If accepted [tag: api_transfer] → outbound retries stop
  → If failed [tag: api_transfer_failed] → outbound retries continue`
          }
        ]
      },
      {
        title: '4. api_transfer_failed Tag',
        content: 'V4.1 introduces a new tag to track leads whose API transfer attempt was made but failed (e.g., the buyer\'s API returned an error or the transfer was rejected for an unexpected reason).',
        subsections: [
          {
            title: 'New Tag: api_transfer_failed',
            table: {
              headers: ['Tag Name', 'Trigger', 'Behavior After Tagging'],
              rows: [
                ['api_transfer_failed', 'Applied when the FDR Server attempts an API transfer to the buyer and the transfer fails (not a duplicate or state rejection — those have their own tags).', 'Lead continues in the outbound call retry workflow. No redirect to DBP or DQ page.'],
              ]
            }
          },
          {
            title: 'Full Tag Reference (V4.1)',
            table: {
              headers: ['Tag', 'Meaning'],
              rows: [
                ['api_transfer', 'Buyer accepted the lead via API transfer. Lead redirected to Dynamic Buyer Page.'],
                ['api_transfer_failed', 'NEW IN V4.1. API transfer was attempted but failed. Lead remains in retry flow.'],
                ['duplicate_rejection', 'Buyer rejected the lead as a duplicate. Lead enters Results Page + Retell call flow.'],
                ['not_qualified', 'Lead disqualified (debt < $7k) or buyer rejected due to state not serviced. Lead sent to DQ page or graceful exit.'],
              ]
            }
          }
        ]
      },
      {
        title: '5. Warm Transfer Calling for NDR API-Transferred Leads',
        content: 'In V4, once a lead was sent via API transfer, no further outbound calls were made. In V4.1, leads API-transferred to NDR are actively called with warm transfer attempts by Jessica.',
        subsections: [
          {
            title: 'V4.1 Behavior',
            items: [
              'After a lead is API-transferred to NDR (tag: api_transfer), the system initiates outbound Retell calls to the lead.',
              'Jessica attempts to warm transfer the lead to NDR on each call.',
              'Calling continues until NDR\'s dashboard shows that NDR has started contacting the lead independently.',
              'Once NDR is actively pursuing the lead, the FDR outbound call attempts stop.',
            ]
          },
          {
            title: 'Stop Condition',
            content: 'The FDR system monitors NDR\'s dashboard status for the lead. When the status indicates NDR has made contact or begun outreach, the FDR outbound retry workflow is terminated for that lead.',
            diagram: `Lead API-transferred to NDR [tag: api_transfer]
→ FDR initiates outbound Retell calls
→ Jessica attempts warm transfer to NDR on each attempt
→ Loop: check NDR dashboard status
  → If NDR has NOT started contact → continue calling
  → If NDR HAS started contact → stop outbound attempts`
          }
        ]
      },
      {
        title: '6. API Transfer Timeframe Extended to 11:30 PM EST',
        content: 'In V4, leads were eligible for immediate API transfer (upon Array form submission) only between 8:00 AM – 9:30 PM EST. Leads submitting after 9:30 PM were tagged retell_after_hrs and entered the outbound call retry flow without an initial API transfer.\n\nIn V4.1, the API transfer window is extended by 2 hours.',
        subsections: [
          {
            title: 'Window Comparison',
            table: {
              headers: ['Version', 'API Transfer Window (EST)', 'After-Hours Threshold'],
              rows: [
                ['V4', '8:00 AM – 9:30 PM', 'Leads submitting after 9:30 PM tagged retell_after_hrs'],
                ['V4.1', '8:00 AM – 11:30 PM', 'Leads submitting after 11:30 PM tagged retell_after_hrs'],
              ]
            }
          },
          {
            title: 'Impact',
            items: [
              'Leads submitting between 9:30 PM and 11:30 PM now receive an immediate API transfer instead of entering the after-hours retry flow.',
              'This reduces the number of leads that enter the after-hours path and increases the share of leads receiving real-time buyer routing.',
              'Combined with Change 3, the after-hours leads who do enter the retry flow are now guaranteed an API transfer attempt after 4 calls.',
            ]
          }
        ]
      }
    ]
  }
}

function TableComponent({ headers, rows }) {
  return (
    <div style={{ overflowX: 'auto', marginTop: 12, marginBottom: 12 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, lineHeight: 1.5 }}>
        <thead>
          <tr style={{ background: '#1a2535' }}>
            {headers.map(h => (
              <th key={h} style={{
                padding: '10px 14px',
                textAlign: 'left',
                color: '#7eb8f7',
                fontWeight: 600,
                borderBottom: '2px solid #2d3f56',
                whiteSpace: 'nowrap',
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? '#111c2b' : '#0d1623' }}>
              {row.map((cell, j) => (
                <td key={j} style={{
                  padding: '9px 14px',
                  borderBottom: '1px solid #1e2e40',
                  verticalAlign: 'top',
                  fontSize: j === 0 ? 13 : 14,
                  fontFamily: j === 0 ? 'monospace' : 'inherit',
                  color: j === 0 ? '#7eb8f7' : '#c9d8ea',
                }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function DiagramBlock({ text }) {
  return (
    <div style={{
      background: '#0a111a',
      border: '1px solid #2d3f56',
      borderRadius: 6,
      padding: '16px 20px',
      marginTop: 12,
      fontFamily: 'monospace',
      fontSize: 13,
      color: '#7eb8f7',
      lineHeight: 1.8,
      whiteSpace: 'pre-wrap',
    }}>{text}</div>
  )
}

function CodeBlock({ text }) {
  return (
    <div style={{
      background: '#080f18',
      border: '1px solid #1e3a52',
      borderRadius: 6,
      padding: '16px 20px',
      marginTop: 12,
      fontFamily: 'monospace',
      fontSize: 12.5,
      color: '#a8d0f0',
      lineHeight: 1.75,
      whiteSpace: 'pre-wrap',
      overflowX: 'auto',
    }}>{text}</div>
  )
}

function Section({ section }) {
  return (
    <div style={{ marginBottom: 32 }}>
      <h3 style={{
        color: '#e8f0fa',
        fontSize: 17,
        fontWeight: 600,
        marginBottom: 12,
        paddingBottom: 8,
        borderBottom: '1px solid #1e2e40',
      }}>{section.title}</h3>

      {section.content && (
        <p style={{ color: '#a8bcd4', lineHeight: 1.75, whiteSpace: 'pre-line', marginBottom: 12 }}>
          {section.content}
        </p>
      )}
      {section.table && <TableComponent headers={section.table.headers} rows={section.table.rows} />}
      {section.diagram && <DiagramBlock text={section.diagram} />}
      {section.code && <CodeBlock text={section.code} />}
      {section.items && (
        <ul style={{ paddingLeft: 20, margin: '8px 0' }}>
          {section.items.map((item, i) => (
            <li key={i} style={{ color: '#a8bcd4', marginBottom: 6, lineHeight: 1.65 }}>{item}</li>
          ))}
        </ul>
      )}

      {section.subsections && section.subsections.map((sub, i) => (
        <div key={i} style={{ marginTop: 20 }}>
          <h4 style={{ color: '#7eb8f7', fontSize: 15, fontWeight: 600, marginBottom: 8 }}>{sub.title}</h4>
          {sub.content && (
            <p style={{ color: '#a8bcd4', lineHeight: 1.75, whiteSpace: 'pre-line', marginBottom: 10 }}>
              {sub.content}
            </p>
          )}
          {sub.items && (
            <ul style={{ paddingLeft: 20, margin: '8px 0' }}>
              {sub.items.map((item, j) => (
                <li key={j} style={{ color: '#a8bcd4', marginBottom: 6, lineHeight: 1.65 }}>{item}</li>
              ))}
            </ul>
          )}
          {sub.table && <TableComponent headers={sub.table.headers} rows={sub.table.rows} />}
          {sub.diagram && <DiagramBlock text={sub.diagram} />}
          {sub.code && <CodeBlock text={sub.code} />}
        </div>
      ))}
    </div>
  )
}

export default function Docs() {
  const [activeVersion, setActiveVersion] = useState(CURRENT_VERSION)
  const version = versionData[activeVersion]

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0b1422',
      color: '#c9d8ea',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    }}>
      {/* Header */}
      <div style={{
        background: '#0d1b2e',
        borderBottom: '1px solid #1e2e40',
        padding: '20px 32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 20, fontWeight: 700, color: '#e8f0fa', letterSpacing: '-0.3px' }}>
              Final Debt Relief
            </span>
            <span style={{
              background: '#1a3a5c',
              color: '#7eb8f7',
              fontSize: 11,
              fontWeight: 600,
              padding: '2px 8px',
              borderRadius: 4,
              letterSpacing: '0.5px',
            }}>DOCS</span>
          </div>
          <div style={{ color: '#4a6a8a', fontSize: 12, marginTop: 2 }}>
            Lead Acquisition & Processing System
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: '#4a6a8a', fontSize: 12 }}>Current:</span>
          <span style={{
            background: '#0e3d1f',
            color: '#4caf76',
            fontSize: 12,
            fontWeight: 600,
            padding: '3px 10px',
            borderRadius: 4,
            border: '1px solid #1a5c30',
          }}>{CURRENT_VERSION}</span>
        </div>
      </div>

      <div style={{ display: 'flex', maxWidth: 1200, margin: '0 auto' }}>
        {/* Sidebar */}
        <div style={{
          width: 220,
          flexShrink: 0,
          padding: '32px 0',
          borderRight: '1px solid #1e2e40',
          position: 'sticky',
          top: 69,
          height: 'calc(100vh - 69px)',
          overflowY: 'auto',
        }}>
          <div style={{ padding: '0 16px', marginBottom: 12 }}>
            <span style={{ color: '#4a6a8a', fontSize: 11, fontWeight: 600, letterSpacing: '0.8px', textTransform: 'uppercase' }}>
              Versions
            </span>
          </div>
          {versions.map(v => {
            const vd = versionData[v]
            const isCurrent = v === CURRENT_VERSION
            const isActive = v === activeVersion
            return (
              <button
                key={v}
                onClick={() => setActiveVersion(v)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  padding: '10px 16px',
                  background: isActive ? '#0d2540' : 'transparent',
                  border: 'none',
                  borderLeft: isActive ? '3px solid #3a7fd4' : '3px solid transparent',
                  color: isActive ? '#e8f0fa' : vd.placeholder ? '#3a5a78' : '#7a9ab8',
                  fontSize: 14,
                  fontWeight: isActive ? 600 : 400,
                  textAlign: 'left',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                <span>{v}</span>
                {isCurrent && (
                  <span style={{
                    background: '#0e3d1f',
                    color: '#4caf76',
                    fontSize: 9,
                    fontWeight: 700,
                    padding: '1px 5px',
                    borderRadius: 3,
                    letterSpacing: '0.3px',
                  }}>LIVE</span>
                )}
                {vd.placeholder && !isCurrent && (
                  <span style={{ color: '#2d4a62', fontSize: 10 }}>Soon</span>
                )}
              </button>
            )
          })}
        </div>

        {/* Main content */}
        <div style={{ flex: 1, padding: '32px 40px', minWidth: 0 }}>
          {/* Version header */}
          <div style={{ marginBottom: 32 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <h1 style={{ color: '#e8f0fa', fontSize: 26, fontWeight: 700, margin: 0 }}>
                {version.label}
              </h1>
              {activeVersion === CURRENT_VERSION && (
                <span style={{
                  background: '#0e3d1f',
                  color: '#4caf76',
                  fontSize: 12,
                  fontWeight: 600,
                  padding: '3px 10px',
                  borderRadius: 4,
                  border: '1px solid #1a5c30',
                }}>Current Version</span>
              )}
            </div>
            {version.date && (
              <div style={{ color: '#4a6a8a', fontSize: 13, marginTop: 4 }}>{version.date}</div>
            )}
            {version.summary && (
              <p style={{
                color: '#7a9ab8',
                fontSize: 14,
                lineHeight: 1.65,
                marginTop: 12,
                padding: '12px 16px',
                background: '#0d1b2e',
                borderRadius: 6,
                border: '1px solid #1e2e40',
              }}>{version.summary}</p>
            )}
          </div>

          {/* Placeholder state */}
          {version.placeholder && (
            <div style={{ textAlign: 'center', padding: '80px 40px', color: '#2d4a62' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#3a5a78', marginBottom: 8 }}>
                Documentation Coming Soon
              </div>
              <div style={{ fontSize: 14 }}>
                {activeVersion} documentation will be published here.
              </div>
            </div>
          )}

          {/* Sections */}
          {!version.placeholder && version.sections.map((section, i) => (
            <Section key={i} section={section} />
          ))}
        </div>
      </div>
    </div>
  )
}
